/**
 * Claude Document Transcriber
 *
 * Uses Claude's vision capabilities to transcribe PDF and image attachments.
 * Also supports text extraction from DOCX, TXT, MD, CSV, JSON, XML, HTML files.
 * Provides EXACT verbatim transcription preserving original language.
 * For non-text files, provides detailed visual descriptions.
 */

import {
  DOCUMENT_ANALYSIS_LADDER,
} from '../config/modelTiers.generated';
import type { TokenUsageMetadata } from '../components/GlobalChatSystem/types';
import {
  CLAUDE_DOCUMENT_ANALYSIS_MODEL,
} from '../config/agenticWorkflowConfig';
import { runTaskStream } from './openRouterClient';
import { knownAccountHint } from './accountHint';
import { SERVER_METER_HEADERS } from './proxyMeter';
import { proxyAuthHeaders } from './proxyAuth';
/**
 * mammoth, xlsx and jszip are loaded ON DEMAND, inside the async extractors
 * that use them (2026-09-09).
 *
 * They were static imports, and this module is reached from the chat engine,
 * which App.tsx imports eagerly for the dock that renders on every route — so
 * a signed-out visitor to the home page downloaded and parsed the whole
 * document toolchain before the hero could paint. mammoth alone drags in
 * xmldom, dingbat-to-unicode, bluebird and underscore.
 *
 * Every extractor here is already async and already awaits file.arrayBuffer(),
 * so the import costs nothing extra at the one moment it is actually needed,
 * and nothing at all for the visitors who never open a document.
 *
 * Deliberately NOT React.lazy/Suspense: this is a plain dynamic import inside
 * an async function, so it cannot reproduce the first-mount Suspense stall
 * documented in App.tsx.
 */

/** Ledger-row summary for the proxy's server meter (slice 2, 2026-09-15). */
const DOC_ANALYSIS_METER_SUMMARY = 'document analysis';



// Claude model with vision capabilities
// 2026-05-10 (Miles directive): Opus-4-7 across both branches. NO Sonnet/Haiku
// in primary OR fallback. Cross-provider performance ladder (GPT-5.5 →
// Gemini-3.1-Pro) handles Claude outages via OpenRouter.
const CLAUDE_VISION_MODEL =
  process.env.REACT_APP_ANTHROPIC_VISION_MODEL ||
  CLAUDE_DOCUMENT_ANALYSIS_MODEL;
// (Slice 7, 2026-09-15: the legacy fallback-model constants retired with
// the deploy-skew ladder — the server descends the SSOT tier itself.)

// Note: API key is NOT needed on frontend - Firebase proxy handles authentication server-side

// Transport liveness windows (2026-06-11 directive: no wall-clock cap on
// model completion — these police the TRANSPORT only). Floors are sealed by
// tests/orchestrator/modelCallLiveness.test.ts companions.
// Connect guard: time until response HEADERS (with stream:true these arrive
// before the model emits anything — reachability, never model time).
export const DOC_ANALYZER_CONNECT_GUARD_MS = 120_000;
// Inactivity watchdog: time since the LAST byte on the stream. OpenRouter
// emits keep-alive comments during long upstream processing, so a healthy
// slow vision run resets this continuously; only a dead connection trips it.
export const DOC_ANALYZER_STREAM_INACTIVITY_MS = 120_000;

// Get the Firebase proxy URL for Anthropic API calls (to avoid CORS)
function getAnthropicProxyUrl(): string {
  const override = (process.env.REACT_APP_ANTHROPIC_PROXY_URL || '').trim();
  if (override) return override;
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/anthropicProxy`;
}

/**
 * System prompt for document transcription
 *
 * Key behaviors:
 * - Description: ALWAYS in English (helps users understand foreign documents)
 * - Transcription: ALWAYS in original language (word-for-word, never translate)
 * - Translation is handled by a SEPARATE translate workflow, not this tool
 * - Output is ONE unified professional text block with no section headers
 */
const DOCUMENT_ANALYSIS_PROMPT = `
You are a document reading assistant. For each uploaded document, provide a unified professional output with:
1. An English description paragraph (with metadata woven in naturally)
2. The complete word-for-word transcription in the original language

OUTPUT FORMAT:

This is a [document type] ([filename], [page count]) in [language]. [Continue with 2-4 sentences IN ENGLISH describing the document's visual layout, purpose, and key information. Include details about letterhead, formatting, stamps, signatures, official seals, handwriting vs typed text, etc. For example: "This is an official police report from Peru documenting a vehicle theft incident. The document features official letterhead with the National Police emblem, includes official stamps and signatures from police officials, and contains sections for incident details, complainant information, and vehicle description."]


[COMPLETE WORD-FOR-WORD TRANSCRIPTION HERE - Start directly with the document text, NO header like "TRANSCRIPTION" or "FULL VERBATIM TRANSCRIPTION". Transcribe ALL text exactly as it appears in the ORIGINAL LANGUAGE.]


CRITICAL RULES:

Output Structure:
- First paragraph: English description with metadata (filename, type, pages, language) woven naturally into the text
- Blank lines: Use 2-3 blank lines as separator (NO "---" or section headers)
- Transcription: Start directly with the document text, NO labels or headers

What This Tool Does:
- Description: ALWAYS in English - helps users understand documents in any language
- Transcription: ALWAYS in original language - exact word-for-word copy of the document text

What This Tool Does NOT Do:
- NEVER use section headers like "DOCUMENT DESCRIPTION", "LAYOUT & DESIGN", "CONTENT SUMMARY", "FULL VERBATIM TRANSCRIPTION"
- NEVER translate the transcription - even if user asks "translate this"
- NEVER summarize the transcription - output should match source length
- NEVER skip content - include everything, even repetitive sections

Why No Translation:
Translation is handled by a SEPARATE translation workflow AFTER transcription. This tool's job is to accurately capture what the document says in its original form. If the user wants translation, they will receive it from the translation workflow that runs after this one.

Formatting Rules:
- DO preserve formatting cues like bullets, numbering, indentation
- DO include page numbers, section headers, footnotes, captions
- DO note unclear text with [unclear] or [illegible]
- Multi-page documents: Mark page breaks with "--- Page X ---"
- Forms: Transcribe both field labels AND filled-in values
- Tables: Preserve column/row structure using | delimiters (e.g., | Column1 | Column2 |)
- Handwriting: Transcribe if legible, mark uncertain words with [?]

For Documents WITHOUT Text (Images, Diagrams, Photos):
Start with the English description paragraph, then provide:
- What the image depicts (subject matter, scene, objects)
- Colors, composition, and visual style
- Any visible text, labels, or annotations (transcribe these in original language)

For Mixed Content (Documents with Images + Text):
1. Start with the English description paragraph (with metadata)
2. Add blank lines as separator
3. Transcribe all text verbatim in original language
4. For embedded images/figures, insert: [IMAGE: detailed description]
`;

export interface DocumentAnalysisParams {
  /** Array of files to analyze (PDFs, images, or documents) */
  files: File[];
  /** Optional additional context from the user */
  userQuery?: string;
  /** Optional abort signal */
  signal?: AbortSignal;
  /**
   * Called as a scanned document is rendered and transcribed, so the caller
   * can say where it is instead of showing a spinner for several minutes.
   */
  onProgress?: (info: TranscriptionProgress) => void;
}

export interface DocumentAnalysisResult {
  /** The analysis text from Claude */
  analysis: string;
  /** Number of files analyzed */
  filesAnalyzed: number;
  /** Any files that couldn't be processed */
  skippedFiles: string[];
  /** Token usage metadata for display in the UI */
  tokenUsage?: TokenUsageMetadata;
  /**
   * RAW extracted document text, complete and UNTRUNCATED (2026-08-11).
   *
   * `analysis` is the model's prose ABOUT the document, built from a copy
   * capped at MAX_EXTRACTED_CHARS so the summary prompt stays bounded. That
   * cap is right for summarising and catastrophic for transforming: a
   * 200-page expediente sent for translation reached the translator as ~14
   * pages of someone else's summary, and the client got a 10-page
   * "translation" of a 200-page file.
   *
   * Tools that must operate on the WHOLE document (translation above all)
   * read this instead. Empty only when the file yielded no text at all —
   * scanned PDFs are transcribed (see transcribeScannedPdf) and populate this
   * like any other document.
   */
  extractedFullText?: string;
  /**
   * Where extractedFullText came from.
   *
   * 'ocr' means it was transcribed from page images, so it can carry
   * recognition errors that an embedded text layer cannot. Callers that show
   * the client a fidelity claim must say so.
   */
  fullTextSource?: 'text-layer' | 'ocr' | 'mixed';
}

// ============================================================================
// File Type Detection
// ============================================================================

/** Image file extensions that Claude vision can process */
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];

/** PDF extension */
const PDF_EXTENSIONS = ['pdf'];

/** DOCX extension (modern Word) - uses mammoth for extraction */
const DOCX_EXTENSIONS = ['docx'];

/** Legacy DOC extension (old Word) - best effort */
const DOC_EXTENSIONS = ['doc'];

/** Plain text file extensions - read as text directly */
const TEXT_EXTENSIONS = ['txt', 'md', 'csv', 'json', 'xml', 'html', 'htm', 'tsv'];

/** Spreadsheet extensions (Excel) - uses SheetJS for extraction */
const XLSX_EXTENSIONS = ['xlsx', 'xls', 'xlsm', 'ods'];

/** Presentation extensions (PowerPoint) - uses JSZip-based slide-XML extraction */
const PPTX_EXTENSIONS = ['pptx', 'ppt', 'odp'];

/** RTF extension - best effort text extraction */
const RTF_EXTENSIONS = ['rtf'];

/** All supported extensions */
const ALL_SUPPORTED_EXTENSIONS = [
  ...IMAGE_EXTENSIONS,
  ...PDF_EXTENSIONS,
  ...DOCX_EXTENSIONS,
  ...DOC_EXTENSIONS,
  ...TEXT_EXTENSIONS,
  ...RTF_EXTENSIONS,
  ...XLSX_EXTENSIONS,
  ...PPTX_EXTENSIONS,
];

/** Get file extension from filename */
export function getExtension(filename: string): string {
  return (filename.toLowerCase().split('.').pop() || '').toLowerCase();
}

/** Check if file is an image */
export function isImageFile(file: File): boolean {
  const ext = getExtension(file.name);
  if (IMAGE_EXTENSIONS.includes(ext)) return true;
  return file.type.startsWith('image/');
}

/** Check if file is a PDF */
export function isPdfFile(file: File): boolean {
  const ext = getExtension(file.name);
  if (PDF_EXTENSIONS.includes(ext)) return true;
  return file.type === 'application/pdf';
}

/** Check if file is a DOCX (modern Word) */
export function isDocxFile(file: File): boolean {
  const ext = getExtension(file.name);
  if (DOCX_EXTENSIONS.includes(ext)) return true;
  return file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}

/** Check if file is a DOC (legacy Word) */
function isDocFile(file: File): boolean {
  const ext = getExtension(file.name);
  if (DOC_EXTENSIONS.includes(ext)) return true;
  return file.type === 'application/msword';
}

/** Check if file is plain text */
export function isTextFile(file: File): boolean {
  const ext = getExtension(file.name);
  if (TEXT_EXTENSIONS.includes(ext)) return true;
  return file.type.startsWith('text/') || file.type === 'application/json' || file.type === 'application/xml';
}

/** Check if file is RTF */
export function isRtfFile(file: File): boolean {
  const ext = getExtension(file.name);
  if (RTF_EXTENSIONS.includes(ext)) return true;
  return file.type === 'application/rtf' || file.type === 'text/rtf';
}

/** Check if file is a spreadsheet (XLSX / XLS / ODS) */
export function isXlsxFile(file: File): boolean {
  const ext = getExtension(file.name);
  if (XLSX_EXTENSIONS.includes(ext)) return true;
  return (
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel' ||
    file.type === 'application/vnd.oasis.opendocument.spreadsheet'
  );
}

/** Check if file is a presentation (PPTX / PPT / ODP) */
export function isPptxFile(file: File): boolean {
  const ext = getExtension(file.name);
  if (PPTX_EXTENSIONS.includes(ext)) return true;
  return (
    file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    file.type === 'application/vnd.ms-powerpoint' ||
    file.type === 'application/vnd.oasis.opendocument.presentation'
  );
}

/**
 * Check if a file type is supported for analysis
 */
export function isSupportedFileType(file: File): boolean {
  const ext = getExtension(file.name);
  if (ALL_SUPPORTED_EXTENSIONS.includes(ext)) return true;

  // Check by MIME type as fallback
  if (file.type.startsWith('image/')) return true;
  if (file.type === 'application/pdf') return true;
  if (file.type.startsWith('text/')) return true;
  if (file.type === 'application/json') return true;
  if (file.type === 'application/xml') return true;
  if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true;
  if (file.type === 'application/msword') return true;
  if (file.type === 'application/rtf') return true;
  // Spreadsheets
  if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return true;
  if (file.type === 'application/vnd.ms-excel') return true;
  if (file.type === 'application/vnd.oasis.opendocument.spreadsheet') return true;
  // Presentations
  if (file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return true;
  if (file.type === 'application/vnd.ms-powerpoint') return true;
  if (file.type === 'application/vnd.oasis.opendocument.presentation') return true;

  return false;
}

// ============================================================================
// File Content Extraction
// ============================================================================

/**
 * Convert a File to a base64 string for Claude's vision API
 */
export async function fileToBase64(file: File): Promise<{ base64: string; mediaType: string } | null> {
  console.log(`[DocAnalyzer] fileToBase64 called for: ${file.name}, type: ${file.type}, size: ${file.size}`);
  try {
    // Prefer FileReader to avoid manual binary string building (more memory-safe on large files).
    const base64FromReader = await new Promise<string | null>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        console.log(`[DocAnalyzer] FileReader onload - result type: ${typeof reader.result}`);
        if (typeof reader.result === 'string') {
          const commaIndex = reader.result.indexOf(',');
          const base64Data = commaIndex >= 0 ? reader.result.slice(commaIndex + 1) : null;
          console.log(`[DocAnalyzer] Base64 extracted - length: ${base64Data?.length || 0}`);
          resolve(base64Data);
        } else {
          console.warn(`[DocAnalyzer] FileReader result is not a string: ${typeof reader.result}`);
          resolve(null);
        }
      };
      reader.onerror = (err) => {
        console.error(`[DocAnalyzer] FileReader error:`, err);
        resolve(null);
      };
      reader.readAsDataURL(file);
    });

    if (!base64FromReader) {
      console.warn(`[DocAnalyzer] Failed to get base64 data for ${file.name}`);
      return null;
    }

    // Determine media type from file metadata first, then from extension as a fallback.
    let mediaType = file.type;
    if (!mediaType || mediaType === 'application/octet-stream') {
      const ext = getExtension(file.name);
      switch (ext) {
        case 'pdf': mediaType = 'application/pdf'; break;
        case 'png': mediaType = 'image/png'; break;
        case 'jpg':
        case 'jpeg': mediaType = 'image/jpeg'; break;
        case 'gif': mediaType = 'image/gif'; break;
        case 'webp': mediaType = 'image/webp'; break;
        default: mediaType = 'application/octet-stream'; break;
      }
    }

    console.log(`[DocAnalyzer] fileToBase64 success for ${file.name} - mediaType: ${mediaType}, base64Length: ${base64FromReader.length}`);
    return { base64: base64FromReader, mediaType };
  } catch (err) {
    console.error(`[DocAnalyzer] Failed to convert ${file.name} to base64:`, err);
    return null;
  }
}

/**
 * Extract text from an XLSX / XLS / ODS spreadsheet using SheetJS.
 * Returns one section per worksheet with rows rendered as CSV-style lines,
 * which preserves both the column layout and the cell values for Claude.
 */
export async function extractTextFromXlsx(file: File): Promise<string | null> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
    const sections: string[] = [];
    const MAX_ROWS_PER_SHEET = 2000; // guard against pathologically large workbooks
    let totalCells = 0;
    const MAX_TOTAL_CELLS = 20_000;
    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) continue;
      // CSV rendering preserves structure better than plain JSON dump for text-only Claude context.
      // `blankrows: false` drops purely empty rows which are common in legal wage-loss templates.
      const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false, forceQuotes: false });
      if (!csv || !csv.trim()) continue;
      const rows = csv.split('\n');
      const clipped = rows.length > MAX_ROWS_PER_SHEET ? rows.slice(0, MAX_ROWS_PER_SHEET) : rows;
      totalCells += clipped.reduce((acc, line) => acc + (line.match(/,/g)?.length ?? 0) + 1, 0);
      const clipNote = rows.length > MAX_ROWS_PER_SHEET
        ? `\n[... ${rows.length - MAX_ROWS_PER_SHEET} additional rows truncated for context budget ...]`
        : '';
      sections.push(`[Sheet: ${sheetName}]\n${clipped.join('\n')}${clipNote}`);
      if (totalCells > MAX_TOTAL_CELLS) {
        sections.push(`[...remaining sheets truncated; total cells read exceeded ${MAX_TOTAL_CELLS}]`);
        break;
      }
    }
    const combined = sections.join('\n\n').trim();
    if (combined.length === 0) {
      console.warn(`[DocAnalyzer] XLSX ${file.name} produced no extractable text`);
      return null;
    }
    console.log(`[DocAnalyzer] Extracted ${combined.length} chars from XLSX: ${file.name} (${workbook.SheetNames.length} sheets)`);
    return combined;
  } catch (err) {
    console.error(`[DocAnalyzer] Failed to extract text from XLSX ${file.name}:`, err);
    return null;
  }
}

/**
 * Extract text from a PPTX / PPT / ODP presentation using a JSZip-based slide-XML walk.
 * PPTX is an OOXML zip containing ppt/slides/slideN.xml files; each <a:t> element is a
 * run of user-visible text. This is a pragmatic text-only extractor; it ignores shapes,
 * animations, speaker notes, and images. Good enough for Claude to summarize the slides
 * without pulling in a heavyweight native renderer.
 */
export async function extractTextFromPptx(file: File): Promise<string | null> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(arrayBuffer);
    // Collect slide XML files in sorted order (slide1.xml, slide2.xml, ...)
    const slideEntries = Object.keys(zip.files)
      .filter((name) => /^ppt\/slides\/slide\d+\.xml$/.test(name))
      .sort((a, b) => {
        const ai = parseInt(a.match(/slide(\d+)\.xml/)?.[1] ?? '0', 10);
        const bi = parseInt(b.match(/slide(\d+)\.xml/)?.[1] ?? '0', 10);
        return ai - bi;
      });
    if (slideEntries.length === 0) {
      // Fallback: some .ppt / legacy / ODP layouts use different paths
      const fallbackCandidates = Object.keys(zip.files).filter((n) =>
        /slide\d+\.xml$/i.test(n) || /content\.xml$/i.test(n)
      );
      if (fallbackCandidates.length === 0) {
        console.warn(`[DocAnalyzer] PPTX ${file.name} contained no slide XMLs — may be legacy .ppt or password-protected`);
        return null;
      }
      slideEntries.push(...fallbackCandidates);
    }
    const slideTexts: string[] = [];
    for (let i = 0; i < slideEntries.length; i++) {
      const entry = zip.files[slideEntries[i]];
      if (!entry) continue;
      const xml = await entry.async('string');
      // Extract visible text runs. Two flavors:
      //   OOXML PPTX: <a:t>text</a:t>
      //   ODP:        <text:p>text</text:p> + <text:span>text</text:span>
      const ooxmlMatches = Array.from(xml.matchAll(/<a:t[^>]*>([\s\S]*?)<\/a:t>/g));
      const odpMatches = Array.from(xml.matchAll(/<text:(?:p|span)[^>]*>([\s\S]*?)<\/text:(?:p|span)>/g));
      const texts = [...ooxmlMatches, ...odpMatches]
        .map((m) => m[1]
          .replace(/<[^>]+>/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&apos;/g, "'")
          .replace(/\s+/g, ' ')
          .trim())
        .filter((t) => t.length > 0);
      if (texts.length === 0) continue;
      slideTexts.push(`[Slide ${i + 1}]\n${texts.join('\n')}`);
    }
    // Also include speaker notes when present
    const notesEntries = Object.keys(zip.files)
      .filter((name) => /^ppt\/notesSlides\/notesSlide\d+\.xml$/.test(name))
      .sort();
    for (const noteName of notesEntries) {
      const entry = zip.files[noteName];
      if (!entry) continue;
      const xml = await entry.async('string');
      const matches = Array.from(xml.matchAll(/<a:t[^>]*>([\s\S]*?)<\/a:t>/g));
      const text = matches
        .map((m) => m[1].replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim())
        .filter((t) => t.length > 0)
        .join(' ');
      if (text) {
        const slideNum = noteName.match(/notesSlide(\d+)\.xml/)?.[1] ?? '?';
        slideTexts.push(`[Speaker notes, slide ${slideNum}]\n${text}`);
      }
    }
    const combined = slideTexts.join('\n\n').trim();
    if (combined.length === 0) {
      console.warn(`[DocAnalyzer] PPTX ${file.name} produced no extractable text`);
      return null;
    }
    console.log(`[DocAnalyzer] Extracted ${combined.length} chars from PPTX: ${file.name} (${slideEntries.length} slides)`);
    return combined;
  } catch (err) {
    console.error(`[DocAnalyzer] Failed to extract text from PPTX ${file.name}:`, err);
    return null;
  }
}

/**
 * Extract text from a DOCX file using mammoth
 */
export async function extractTextFromDocx(file: File): Promise<string | null> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const { default: mammoth } = await import('mammoth');
    const result = await mammoth.extractRawText({ arrayBuffer });
    if (result.value && result.value.trim().length > 0) {
      console.log(`[DocAnalyzer] Extracted ${result.value.length} chars from DOCX: ${file.name}`);
      return result.value;
    }
    return null;
  } catch (err) {
    console.error(`[DocAnalyzer] Failed to extract text from DOCX ${file.name}:`, err);
    return null;
  }
}

/**
 * Extract text from a plain text file
 */
export async function extractTextFromTextFile(file: File): Promise<string | null> {
  try {
    const text = await file.text();
    if (text && text.trim().length > 0) {
      console.log(`[DocAnalyzer] Read ${text.length} chars from text file: ${file.name}`);
      return text;
    }
    return null;
  } catch (err) {
    console.error(`[DocAnalyzer] Failed to read text file ${file.name}:`, err);
    return null;
  }
}

/**
 * Best-effort extraction from RTF files
 * RTF has some plain text content that can be extracted with simple regex
 */
export async function extractTextFromRtf(file: File): Promise<string | null> {
  try {
    const rawText = await file.text();
    // Strip RTF control sequences (basic extraction)
    // This is a simplified approach - complex RTF may not extract perfectly
    let text = rawText
      .replace(/\\[a-z]+[0-9]*\s?/gi, '') // Remove control words
      .replace(/[{}]/g, '') // Remove braces
      .replace(/\\\\/g, '\\') // Unescape backslashes
      .replace(/\\'/g, "'") // Unescape quotes
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
    
    if (text && text.length > 50) {
      console.log(`[DocAnalyzer] Extracted ${text.length} chars from RTF: ${file.name}`);
      return text;
    }
    return null;
  } catch (err) {
    console.error(`[DocAnalyzer] Failed to extract text from RTF ${file.name}:`, err);
    return null;
  }
}

// ============================================================================
// Message Content Building
// ============================================================================

/**
 * Build the message content array for Claude with files
 * Handles images/PDFs via vision, and text-based files as text blocks
 */
/**
 * The in-band note that ends a truncated excerpt.
 *
 * WHY THE WORDING IS LOAD-BEARING (INCIDENT-20260824): the previous note said
 * "truncated for summary length; ask about a specific section/page for detail
 * on the rest", and the live answer read it as evidence the document had not
 * arrived — telling a client-facing user "pages 2 through 30 did not reach me
 * in workable text… I just don't have it in front of me" and asking them to
 * re-upload the file in ten-page blocks. THE COMPLETE TEXT WAS ALREADY READ
 * AND BANKED (57,883 characters of a 30-page scan) — it simply is not in THIS
 * prompt, by design, because the summary prompt is capped.
 *
 * So the note now states three things the model must not get wrong: how much
 * of the document this excerpt is, that the REST IS HELD (not missing), and
 * that the way to reach it is a whole-document task — never asking the user to
 * split or re-upload a file the system has already read in full.
 */
export function buildExcerptTruncationNote(sentChars: number, totalChars: number): string {
  const pct = Math.max(1, Math.round((sentChars / Math.max(1, totalChars)) * 100));
  return `\n\n[EXCERPT — this is the first ${pct}% of the document (${sentChars.toLocaleString('en-US')} of ${totalChars.toLocaleString('en-US')} characters). `
    + `THE COMPLETE TEXT HAS BEEN READ AND IS HELD BY THE SYSTEM; the remainder is omitted from THIS prompt only, to bound its length. `
    + `Do NOT say the rest of the document is missing, unreadable, or "did not reach you", and do NOT ask the user to re-upload, split, or re-send the file — the system already has all of it. `
    + `Do NOT infer that something is absent from the document merely because it is not in this excerpt. `
    + `To work with the whole document, run a whole-document task (translate / format / summarise the full document), which reads the complete text, or ask about a specific page or section.]`;
}

async function buildMessageContent(files: File[], userQuery?: string, signal?: AbortSignal, onProgress?: (info: TranscriptionProgress) => void): Promise<{ content: unknown[]; skipped: string[]; textExtracted: string[]; hasPdfFiles: boolean; fullTexts: Array<{ name: string; text: string }>; ocrFiles: string[] }> {
  console.log(`[DocAnalyzer] buildMessageContent called with ${files?.length || 0} files`);

  // Log details about each file
  if (files && files.length > 0) {
    files.forEach((file, idx) => {
      console.log(`[DocAnalyzer] File ${idx}: name=${file.name}, type=${file.type}, size=${file.size}, isFile=${file instanceof File}`);
    });
  }

  const content: unknown[] = [];
  const skipped: string[] = [];
  const textExtracted: string[] = [];
  // Untruncated per-file text, kept separately from the (capped) summarizer
  // copy pushed into `content`. See DocumentAnalysisResult.extractedFullText.
  const fullTexts: Array<{ name: string; text: string }> = [];
  // Files whose text came from transcribing page images rather than a text
  // layer. Downstream callers tell the client, because OCR text can carry
  // recognition errors that extracted text cannot.
  const ocrFiles: string[] = [];
  let hasPdfFiles = false;

  // Add user query text first if provided
  if (userQuery) {
    content.push({
      type: 'text',
      text: `User's context: ${userQuery}\n\nPlease transcribe the attached document(s) exactly as they appear, in their original language.`,
    });
  }
  
  // Maximum file size (20MB for vision, 5MB for text)
  const maxVisionSize = 20 * 1024 * 1024;
  const maxTextSize = 5 * 1024 * 1024;
  
  // Process each file
  for (const file of files) {
    if (!isSupportedFileType(file)) {
      console.warn(`[DocAnalyzer] Skipping unsupported file type: ${file.name} (${file.type})`);
      skipped.push(`${file.name} (unsupported format)`);
      continue;
    }
    
    // ========================================================================
    // Handle DOCX files (extract text via mammoth)
    // ========================================================================
    if (isDocxFile(file)) {
      if (file.size > maxTextSize) {
        console.warn(`[DocAnalyzer] DOCX too large: ${file.name}`);
        skipped.push(`${file.name} (too large)`);
        continue;
      }
      
      const extractedText = await extractTextFromDocx(file);
      if (extractedText) {
        content.push({
          type: 'text',
          text: `\n--- Document: ${file.name} ---\n${extractedText}\n--- End of ${file.name} ---\n`,
        });
        textExtracted.push(file.name);
        fullTexts.push({ name: file.name, text: extractedText });
      } else {
        skipped.push(`${file.name} (extraction failed)`);
      }
      continue;
    }
    
    // ========================================================================
    // Handle DOC files (legacy Word - best effort, may not work)
    // ========================================================================
    if (isDocFile(file)) {
      console.warn(`[DocAnalyzer] DOC format is legacy. Consider converting to DOCX or PDF: ${file.name}`);
      // DOC is a complex binary format - we can't reliably extract text client-side
      // Skip with helpful message
      skipped.push(`${file.name} (legacy .doc format - please convert to .docx or .pdf)`);
      continue;
    }
    
    // ========================================================================
    // Handle plain text files (TXT, MD, CSV, JSON, XML, HTML)
    // ========================================================================
    if (isTextFile(file)) {
      if (file.size > maxTextSize) {
        console.warn(`[DocAnalyzer] Text file too large: ${file.name}`);
        skipped.push(`${file.name} (too large)`);
        continue;
      }
      
      const extractedText = await extractTextFromTextFile(file);
      if (extractedText) {
        content.push({
          type: 'text',
          text: `\n--- Document: ${file.name} ---\n${extractedText}\n--- End of ${file.name} ---\n`,
        });
        textExtracted.push(file.name);
        fullTexts.push({ name: file.name, text: extractedText });
      } else {
        skipped.push(`${file.name} (empty or unreadable)`);
      }
      continue;
    }
    
    // ========================================================================
    // Handle XLSX / XLS / ODS spreadsheets (SheetJS)
    // ========================================================================
    if (isXlsxFile(file)) {
      if (file.size > maxTextSize) {
        console.warn(`[DocAnalyzer] Spreadsheet too large: ${file.name}`);
        skipped.push(`${file.name} (too large)`);
        continue;
      }
      const extractedText = await extractTextFromXlsx(file);
      if (extractedText) {
        content.push({
          type: 'text',
          text: `\n--- Spreadsheet: ${file.name} ---\n${extractedText}\n--- End of ${file.name} ---\n`,
        });
        textExtracted.push(file.name);
      } else {
        skipped.push(`${file.name} (spreadsheet extraction failed)`);
      }
      continue;
    }

    // ========================================================================
    // Handle PPTX / PPT / ODP presentations (JSZip + slide-XML walk)
    // ========================================================================
    if (isPptxFile(file)) {
      if (file.size > maxTextSize) {
        console.warn(`[DocAnalyzer] Presentation too large: ${file.name}`);
        skipped.push(`${file.name} (too large)`);
        continue;
      }
      const extractedText = await extractTextFromPptx(file);
      if (extractedText) {
        content.push({
          type: 'text',
          text: `\n--- Presentation: ${file.name} ---\n${extractedText}\n--- End of ${file.name} ---\n`,
        });
        textExtracted.push(file.name);
      } else {
        skipped.push(`${file.name} (presentation extraction failed — may be legacy .ppt or password-protected)`);
      }
      continue;
    }

    // ========================================================================
    // Handle RTF files (best effort text extraction)
    // ========================================================================
    if (isRtfFile(file)) {
      if (file.size > maxTextSize) {
        console.warn(`[DocAnalyzer] RTF too large: ${file.name}`);
        skipped.push(`${file.name} (too large)`);
        continue;
      }
      
      const extractedText = await extractTextFromRtf(file);
      if (extractedText) {
        content.push({
          type: 'text',
          text: `\n--- Document: ${file.name} (RTF, partial extraction) ---\n${extractedText}\n--- End of ${file.name} ---\n`,
        });
        textExtracted.push(file.name);
        fullTexts.push({ name: file.name, text: extractedText });
      } else {
        skipped.push(`${file.name} (RTF extraction failed - consider converting to PDF)`);
      }
      continue;
    }
    
    // ========================================================================
    // Handle PDFs and Images (use Claude vision)
    // ========================================================================
    if (isPdfFile(file) || isImageFile(file)) {
      // ── FAST PATH: client-side PDF text extraction (no API, no timeout risk) ──────────
      // For text-based PDFs (the majority of legal/research documents), pdf.js can extract
      // all text instantly in the browser without any network call. Only fall back to the
      // vision API path for scanned/image-only PDFs where client-side extraction returns
      // too little text.
      if (isPdfFile(file)) {
        const clientText = await extractTextFromPdfClientSide(file);
        if (clientText && clientText.length >= 500) {
          // Build a synthetic "already extracted" result — skip the entire API round-trip
          textExtracted.push(file.name);
          // Cap the extracted text sent to the summarizer (MAX_EXTRACTED_CHARS);
          // truncation is noted inline. The full text is banked below.
          const truncated = clientText.length > MAX_EXTRACTED_CHARS;
          const sentText = truncated
            ? clientText.slice(0, MAX_EXTRACTED_CHARS) +
              buildExcerptTruncationNote(MAX_EXTRACTED_CHARS, clientText.length)
            : clientText;
          // Add the extracted text as a regular text block in the messages array
          // so it flows through the same downstream processing as DOCX/RTF text extraction
          content.push({
            type: 'text',
            text: `Content of "${file.name}" (extracted client-side from ${Math.round(file.size / 1024)}KB PDF):\n\n${sentText}`,
          });
          // The cap above bounds the SUMMARY prompt only. Bank the complete
          // text so whole-document tools (translation) never inherit that cap.
          fullTexts.push({ name: file.name, text: clientText });
          console.log(`[DocAnalyzer] Used client-side extraction for ${file.name} (${clientText.length} chars${truncated ? `, sent ${MAX_EXTRACTED_CHARS} after truncation; full text retained for whole-document tools` : ''})`);
          continue; // skip to next file, no API call needed
        }
        // Client-side extraction was insufficient — fall through to vision API
        console.log(`[DocAnalyzer] Client-side extraction insufficient for ${file.name}, using vision API`);
      }

      if (file.size > maxVisionSize) {
        console.warn(`[DocAnalyzer] File too large for vision: ${file.name} (${Math.round(file.size / 1024 / 1024)}MB)`);
        skipped.push(`${file.name} (too large)`);
        continue;
      }
      
      const result = await fileToBase64(file);
      if (!result) {
        skipped.push(`${file.name} (encoding failed - empty file or unsupported type)`);
        continue;
      }
      
      // PDFs use document type, images use image type
      if (isPdfFile(file)) {
        hasPdfFiles = true;

        // ── Scanned PDF: transcribe it to TEXT first (2026-08-13) ───────────
        // Rasterising alone was not enough. Page images appended to THIS
        // request put the transcription inside `analysis` — prose about the
        // document — while extractedFullText stayed empty, so the translate
        // tool correctly refused it and the client got "I couldn't extract any
        // text" from a file we had just read. Transcribing in its own pass
        // yields raw text, and from here a scanned PDF is indistinguishable
        // from a text-layer one.
        const transcript = await transcribeScannedPdf(file, signal, onProgress);
        if (transcript) {
          const clientText = transcript.text;
          const truncated = clientText.length > MAX_EXTRACTED_CHARS;
          const sentText = truncated
            ? clientText.slice(0, MAX_EXTRACTED_CHARS) +
              buildExcerptTruncationNote(MAX_EXTRACTED_CHARS, clientText.length)
            : clientText;
          content.push({
            type: 'text',
            text:
              `Content of "${file.name}" (transcribed from ${transcript.pagesTranscribed} scanned ` +
              `page(s) of ${transcript.totalPages}; this PDF has no text layer):\n\n${sentText}`,
          });
          textExtracted.push(file.name);
          fullTexts.push({ name: file.name, text: clientText });
          ocrFiles.push(file.name);
          console.log(
            `[DocAnalyzer] Used OCR transcription for ${file.name} (${clientText.length} chars from ` +
            `${transcript.pagesTranscribed}/${transcript.totalPages} pages` +
            `${transcript.failedPages.length ? `, ${transcript.failedPages.length} page(s) unreadable` : ''})`,
          );
          continue; // joins the text path — no vision block needed
        }

        // ── Fallback: rasterised PAGES over a raw PDF block (2026-08-13) ─────
        // A scanned 30-page file sent as one `document` block came back as "No
        // document was attached or accessible": the proxy converts the block
        // correctly, but provider-side PDF parsing is not dependable across
        // rungs. Image blocks are, so render the pages ourselves. Falls through
        // to the original document block if rasterisation is unavailable.
        const rasterised = await rasterizePdfPagesClientSide(file);
        if (rasterised && rasterised.pages.length > 0) {
          const { pages, totalPages } = rasterised;
          const partial = pages.length < totalPages;
          content.push({
            type: 'text',
            text:
              `"${file.name}" is a scanned/image-only PDF with no text layer. ` +
              `The following ${pages.length} image(s) are its page(s)` +
              (partial ? ` 1-${pages.length} of ${totalPages}` : '') +
              `, in order. Transcribe every page VERBATIM in the original language, ` +
              `preserving page breaks and numbering.` +
              (partial
                ? ` Only the first ${pages.length} of ${totalPages} pages are included; ` +
                  `say so explicitly at the end of the transcription.`
                : ''),
          });
          for (const pageImage of pages) {
            content.push({
              type: 'image',
              source: {
                type: 'base64',
                media_type: pageImage.mediaType,
                data: pageImage.base64,
              },
            });
          }
          console.log(
            `[DocAnalyzer] Sent ${pages.length} rasterised page image(s) for ${file.name}` +
            (partial ? ` (capped from ${totalPages})` : ''),
          );
          continue;
        }
        console.warn(
          `[DocAnalyzer] Rasterisation unavailable for ${file.name}; sending raw PDF block (provider may not parse it)`,
        );
        content.push({
          type: 'document',
          source: {
            type: 'base64',
            media_type: result.mediaType,
            data: result.base64,
          },
        });
      } else {
        const imageBlock = {
          type: 'image',
          source: {
            type: 'base64',
            media_type: result.mediaType,
            data: result.base64,
          },
        };
        content.push(imageBlock);
        console.log(`[DocAnalyzer] Added IMAGE block for ${file.name} - mediaType: ${result.mediaType}, base64 length: ${result.base64.length}`);
      }

      console.log(`[DocAnalyzer] Added ${file.name} (${result.mediaType}, ${Math.round(file.size / 1024)}KB)`);
      continue;
    }
    
    // Fallback - shouldn't reach here if isSupportedFileType is correct
    console.warn(`[DocAnalyzer] Unhandled file type: ${file.name}`);
    skipped.push(`${file.name} (unhandled format)`);
  }
  
  // Add instruction text at the end
  content.push({
    type: 'text',
    text: 'Please transcribe the document(s) above according to the system instructions. Provide exact verbatim content in the original language.',
  });
  
  return { content, skipped, textExtracted, hasPdfFiles, fullTexts, ocrFiles };
}

// ============================================================================
// Client-Side PDF Text Extraction (fast path — no API call for text-based PDFs)
// ============================================================================

/**
 * A pdf.js text item. `transform` is a 6-element matrix; [4] is x and [5] is
 * the baseline y, both in PDF user space (y grows UPWARD).
 */
export interface PdfTextItem {
  str: string;
  transform?: number[];
  width?: number;
  height?: number;
}

/** A line whose tallest glyph exceeds the body height by this much is a heading. */
export const HEADING_SIZE_RATIO = 1.15;
/** Headings are short. Longer than this and it is a paragraph in a big font. */
export const HEADING_MAX_CHARS = 90;
/** Baseline gap beyond this multiple of the line height starts a new paragraph. */
export const PARAGRAPH_GAP_RATIO = 1.6;
/** Horizontal gap beyond this multiple of the glyph height is a column boundary. */
export const TABLE_CELL_GAP_RATIO = 1.5;
/**
 * Minimum aligned cells for a line to count toward a table. THREE, not two:
 * two-cluster lines are everywhere that is not a table — "Fecha: 03/2022"
 * label/value pairs, and two-column PAGE layouts. Mis-detecting those would
 * mangle prose into pipes, which is worse than leaving a real table flat.
 */
export const TABLE_MIN_COLUMNS = 3;
/** A lone multi-cell line is a coincidence; a table has at least a header and a row. */
export const TABLE_MIN_ROWS = 2;

/**
 * A numbered SUBSECTION heading: "1.1 Scope", "2.3.4 Torque values".
 * At least one dot-decimal, which is what separates it from a numbered body
 * paragraph ("5. Access to the power panel is restricted to...").
 */
export const HEADING_SUBSECTION_PATTERN = /^\d+(?:\.\d+)+[.)]?\s+\S/;
/** A numbered SECTION heading: "1. GENERAL SYSTEM DESCRIPTION". */
export const HEADING_SECTION_PATTERN = /^\d+[.)]\s+\S/;

/** Pages needed before repeated edge lines can be judged to be furniture. */
export const PAGE_FURNITURE_MIN_PAGES = 4;
/** Share of pages a line must appear on to count as a running header/footer. */
export const PAGE_FURNITURE_MIN_SHARE = 0.5;
/** How many lines at each edge of a page can be furniture. */
export const PAGE_FURNITURE_EDGE_LINES = 2;

/**
 * Join the visual lines of ONE paragraph back into one paragraph.
 *
 * WHY (2026-08-20, the 200-page manual): a PDF stores where the typesetter
 * broke each line, not where the author ended each paragraph. We were emitting
 * one line per visual line, and every downstream builder splits on newlines —
 * so the Word file came back with 8,310 paragraphs, 3,192 of which began in
 * mid-sentence with a lowercase word. The deliverable reflows to a different
 * width in a different language, so the source's line breaks are not merely
 * useless, they are actively wrong.
 *
 * A hyphen at a line break is a broken word ("mainte-" + "nance"), and only
 * when the next line resumes in lower case — "SEA-4200" must survive intact.
 */
export function joinWrappedLines(lines: string[]): string {
  let out = '';
  for (const raw of lines) {
    const line = (raw || '').trim();
    if (!line) continue;
    if (!out) {
      out = line;
      continue;
    }
    if (/[\p{L}]-$/u.test(out) && /^[\p{Ll}]/u.test(line)) {
      out = out.slice(0, -1) + line;
      continue;
    }
    out = `${out} ${line}`;
  }
  return out;
}

/**
 * Which heading level a BLOCK of text is, or 0 for body text.
 *
 * Classifying a BLOCK rather than a LINE is the whole point. The old rule ran
 * per line and asked only "is this line taller than the body and under 90
 * characters", which made 320 of 539 headings in the 200-page manual sentence
 * fragments — "side guards and release of the automatic tensioner." was a
 * heading because it happened to be the short last line of a paragraph.
 * Once wrapped lines are joined, that fragment is no longer a block at all,
 * and the length test finally means what it says.
 *
 * The rest are guards against the same class of mistake: a heading does not
 * begin in lower case and does not end in a full stop.
 *
 * Levels are real, too — everything used to be emitted as '## ', which is why
 * all 539 headings landed at the same Word outline level with no hierarchy.
 */
export function headingLevelForBlock(
  text: string,
  options: { tallerThanBody?: boolean; startsBlock?: boolean } = {},
): 0 | 2 | 3 {
  const trimmed = (text || '').trim();
  if (!trimmed || trimmed.length > HEADING_MAX_CHARS) return 0;
  if (trimmed.startsWith('## ')) return 0;
  // Lower case opens a continuation, never a heading.
  if (/^[\p{Ll}]/u.test(trimmed)) return 0;

  const numberedSub = HEADING_SUBSECTION_PATTERN.test(trimmed);
  const numberedSection = HEADING_SECTION_PATTERN.test(trimmed);
  // Sentence-final punctuation ends a sentence, and a heading is not one. The
  // trailing dot of "1." is part of the number, not the end of a sentence.
  if (/[.!?]$/.test(trimmed) && !numberedSub && !numberedSection) return 0;
  if (/[.!?]$/.test(trimmed) && (numberedSub || numberedSection)) return 0;

  if (numberedSub) return 3;
  if (numberedSection) return 2;

  const letters = trimmed.replace(/[^\p{L}]/gu, '');
  const allCaps = letters.length >= 3 && letters === letters.toUpperCase();
  if (allCaps) return 2;
  if (options.tallerThanBody && options.startsBlock !== false) return 2;
  return 0;
}

/**
 * Does this SINGLE LINE stand on its own as a heading?
 *
 * WHY (2026-08-21, measured on the re-run): rejoining wrapped lines fixed 3,192
 * mid-sentence paragraphs, but it also swallowed every heading that sits
 * directly on top of its paragraph with no blank line between them. All 115
 * "SAFETY WARNING" headings in the 200-page manual came out as
 * "SAFETY WARNING Failure to follow the lockout and tagout procedure may..." —
 * the heading absorbed into the warning it was labelling.
 *
 * A vertical gap is not the only paragraph boundary. A line that is
 * self-evidently a heading — an all-caps label, a numbered section title — is
 * one too, and it must terminate the block before it and stand alone.
 *
 * Deliberately narrower than headingLevelForBlock: this decides where to CUT,
 * so it only fires on shapes that cannot be an ordinary sentence.
 */
export function looksLikeStandaloneHeadingLine(text: string): boolean {
  const trimmed = (text || '').trim();
  if (!trimmed || trimmed.length > HEADING_MAX_CHARS) return false;
  if (/[.!?]$/.test(trimmed)) return false;
  // A numbered SUBSECTION ("1.1 Scope") cannot be an ordinary sentence.
  if (HEADING_SUBSECTION_PATTERN.test(trimmed)) return true;
  // A single-level numbered line ("4. Documentation of each intervention must
  // include the date, work order") is the shape of EVERY numbered body
  // paragraph in a technical manual, so the number alone earns nothing —
  // cutting on it split paragraphs in half. Only the capitals decide.
  const letters = trimmed.replace(/[^\p{L}]/gu, '');
  return letters.length >= 3 && letters === letters.toUpperCase();
}

/**
 * Drop the running header and page footer that repeat on every page.
 *
 * WHY (2026-08-20): the 200-page manual's header ("... - Controlled Document -
 * Page 14 of 200") and footer ("... - Test Document 14 / 200") were extracted
 * as ordinary body text, so the translated deliverable carried roughly 284
 * lines of page furniture interleaved through its prose — and paid to
 * translate every one of them.
 *
 * Furniture is identified by REPETITION AT THE PAGE EDGES: normalise digits
 * away (the page number is the only thing that changes), count how many pages
 * carry each edge line, and drop the ones that appear on at least half. Only
 * lines at the very top or bottom are eligible — a phrase repeated mid-body is
 * content, however often it recurs.
 */
export function stripRepeatedPageFurniture(pageTexts: string[]): string[] {
  const pages = pageTexts || [];
  if (pages.length < PAGE_FURNITURE_MIN_PAGES) return pages;

  const normalise = (line: string) =>
    line.trim().replace(/\d+/g, '#').replace(/\s+/g, ' ');

  const pageCount = new Map<string, number>();
  for (const text of pages) {
    const lines = (text || '').split('\n').filter((l) => l.trim());
    const edges = [
      ...lines.slice(0, PAGE_FURNITURE_EDGE_LINES),
      ...lines.slice(-PAGE_FURNITURE_EDGE_LINES),
    ];
    // Once per page, so a line repeated twice on one page cannot self-elect.
    for (const key of new Set(edges.map(normalise))) {
      if (!key) continue;
      pageCount.set(key, (pageCount.get(key) || 0) + 1);
    }
  }

  const threshold = Math.max(2, Math.ceil(pages.length * PAGE_FURNITURE_MIN_SHARE));
  const furniture = new Set(
    [...pageCount.entries()].filter(([, count]) => count >= threshold).map(([key]) => key),
  );
  if (furniture.size === 0) return pages;

  return pages.map((text) => {
    const lines = (text || '').split('\n');
    const keep = lines.map(() => true);
    const contentIdx = lines
      .map((line, idx) => ({ line, idx }))
      .filter(({ line }) => line.trim())
      .map(({ idx }) => idx);
    const edgeIdx = [
      ...contentIdx.slice(0, PAGE_FURNITURE_EDGE_LINES),
      ...contentIdx.slice(-PAGE_FURNITURE_EDGE_LINES),
    ];
    for (const idx of edgeIdx) {
      if (furniture.has(normalise(lines[idx]))) keep[idx] = false;
    }
    const stripped = lines.filter((_, idx) => keep[idx]).join('\n').trim();
    // Never let furniture removal empty a page: that would lose real content
    // on a page that happens to be mostly header.
    return stripped ? stripped : (text || '');
  });
}

function medianOf(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Rebuild a page's text from pdf.js items, PRESERVING LAYOUT.
 *
 * WHY (2026-08-13, formatting feedback on the 45-page financial report): this
 * used to be `items.map(i => i.str).join(' ')` — every page collapsed to a
 * single run-on line. That one line destroyed the document before any other
 * stage could see it:
 *
 * - Headings became mid-sentence text ("...testing purposes. I. EXECUTIVE
 *   SUMMARY 2. The allowance for doubtful accounts..."), so the deliverable had
 *   no headings — there were no heading LINES left to detect.
 * - Paragraph boundaries were gone, so 136 numbered paragraphs arrived as 45
 *   walls of text.
 * - "--- Page N ---" was the only newline on the page, which left the model
 *   free to reflow it inline. It did, the page-break rule (which requires a
 *   standalone marker line, deliberately, so it can never swallow real content)
 *   never matched, and a 45-page source became a 27-page deliverable with
 *   "--- Page 1 ---" sitting at the start of a paragraph.
 *
 * Items carry their position, so the lines are recoverable: group by baseline
 * y, order by x, and read vertical gaps as paragraph breaks and glyph height
 * as emphasis. Pure and side-effect free — it takes an item array, so it is
 * fully testable without a browser, unlike everything else in this file.
 */
export function buildPageTextFromItems(items: PdfTextItem[]): string {
  const usable = (items || []).filter(
    (item) => item && typeof item.str === 'string' && item.str.trim().length > 0,
  );
  if (usable.length === 0) return '';

  const positioned = usable.filter(
    (item) => Array.isArray(item.transform) && item.transform.length >= 6,
  );
  // No geometry (some generators emit none) — the old flat join is all we have.
  if (positioned.length === 0) {
    return usable.map((item) => item.str).join(' ').replace(/\s+/g, ' ').trim();
  }

  const heights = positioned
    .map((item) => Number(item.height) || 0)
    .filter((h) => h > 0);
  const bodyHeight = medianOf(heights) || 10;
  // Half a line height: enough to absorb baseline jitter and subscripts,
  // tight enough that two real lines never merge.
  const lineTolerance = Math.max(1, bodyHeight * 0.5);

  interface Line { y: number; maxHeight: number; items: PdfTextItem[] }
  const lines: Line[] = [];
  for (const item of positioned) {
    const y = Number(item.transform![5]) || 0;
    const height = Number(item.height) || bodyHeight;
    const existing = lines.find((line) => Math.abs(line.y - y) <= lineTolerance);
    if (existing) {
      existing.items.push(item);
      existing.maxHeight = Math.max(existing.maxHeight, height);
    } else {
      lines.push({ y, maxHeight: height, items: [item] });
    }
  }

  // PDF user space grows upward, so reading order is DESCENDING y.
  lines.sort((a, b) => b.y - a.y);

  interface RenderedLine {
    text: string;
    isHeading: boolean;
    /** Tallest glyph on the line exceeded the body height — a heading hint. */
    tallerThanBody: boolean;
    gapBefore: boolean;
    /** Cell texts split at wide horizontal gaps — the raw material for tables. */
    cells: string[];
    /** Left edge of each cell, for cross-row column alignment. */
    cellXs: number[];
  }
  const renderedLines: RenderedLine[] = [];
  let previousY: number | null = null;
  for (const line of lines) {
    const ordered = [...line.items].sort(
      (a, b) => (Number(a.transform![4]) || 0) - (Number(b.transform![4]) || 0),
    );
    const cells: string[] = [];
    const cellXs: number[] = [];
    let cell = '';
    let cellStartX = 0;
    let cursorX: number | null = null;
    for (const item of ordered) {
      const x = Number(item.transform![4]) || 0;
      const gap = cursorX === null ? 0 : x - cursorX;
      // A gap wider than the cell threshold is a COLUMN boundary; wider than a
      // quarter of the glyph height is a missing space (pdf.js often splits
      // words without emitting one).
      if (cell && gap > bodyHeight * TABLE_CELL_GAP_RATIO) {
        cells.push(cell);
        cellXs.push(cellStartX);
        cell = '';
      } else if (cell && gap > bodyHeight * 0.25 && !/\s$/.test(cell) && !/^\s/.test(item.str)) {
        cell += ' ';
      }
      if (!cell) cellStartX = x;
      cell += item.str;
      cursorX = x + (Number(item.width) || 0);
    }
    if (cell) {
      cells.push(cell);
      cellXs.push(cellStartX);
    }
    const cleanXs: number[] = [];
    const cleanCells: string[] = [];
    cells.forEach((c, idx) => {
      const trimmed = c.replace(/\s+/g, ' ').trim();
      if (trimmed) {
        cleanCells.push(trimmed);
        cleanXs.push(cellXs[idx]);
      }
    });
    const text = cleanCells.join(' ');
    if (!text) continue;

    // A vertical gap larger than a line and a half is a paragraph break.
    const gapBefore =
      previousY !== null && previousY - line.y > bodyHeight * PARAGRAPH_GAP_RATIO;
    previousY = line.y;

    // Whether this line is a heading is decided later, on the assembled BLOCK
    // (see headingLevelForBlock). Deciding per line is what turned 320 short
    // paragraph-final lines into headings.
    const tallerThanBody = line.maxHeight > bodyHeight * HEADING_SIZE_RATIO;
    renderedLines.push({
      text,
      isHeading: false,
      tallerThanBody,
      gapBefore,
      cells: cleanCells,
      cellXs: cleanXs,
    });
  }

  // ── Table reconstruction (2026-08-13) ──────────────────────────────────────
  // A run of consecutive lines whose cells ALIGN AT THE SAME X POSITIONS is a
  // table; each becomes a "| a | b | c |" row, which the document builders'
  // existing markdown-table rule renders as a real grid — pipes also survive
  // translation far better than layout ever could.
  //
  // Alignment, NOT vertical spacing, is the run signal: measured on the
  // 45-page report, table rows sit 14pt apart at an 8.6pt body — just over the
  // paragraph-gap threshold — so a run detector keyed on "no gap between rows"
  // matched exactly nothing. Columns aligning within a couple of glyph heights
  // across rows is the property a table actually has and prose does not.
  //
  // Conservative on purpose (TABLE_MIN_COLUMNS / TABLE_MIN_ROWS / alignment):
  // a false positive turns prose into pipes, which is worse than the flat
  // lines it replaces.
  const isTableCandidate = (l: RenderedLine) =>
    !l.isHeading && l.cells.length >= TABLE_MIN_COLUMNS;
  const columnsAlign = (a: RenderedLine, b: RenderedLine) =>
    a.cellXs.length === b.cellXs.length &&
    a.cellXs.every((x, idx) => Math.abs(x - b.cellXs[idx]) <= bodyHeight * 2);
  const rendered: string[] = [];
  for (let i = 0; i < renderedLines.length; ) {
    const line = renderedLines[i];
    if (isTableCandidate(line)) {
      let end = i + 1;
      while (
        end < renderedLines.length &&
        isTableCandidate(renderedLines[end]) &&
        columnsAlign(line, renderedLines[end])
      ) {
        end++;
      }
      if (end - i >= TABLE_MIN_ROWS) {
        if (line.gapBefore && rendered.length) rendered.push('');
        for (let r = i; r < end; r++) {
          rendered.push(`| ${renderedLines[r].cells.join(' | ')} |`);
        }
        i = end;
        continue;
      }
    }
    // Gather the whole paragraph — every wrapped line up to the next vertical
    // gap, heading candidate or table — and emit it as ONE line, because the
    // builders treat every newline as a block boundary.
    const blockLines: string[] = [line.text];
    let tallerThanBody = Boolean(line.tallerThanBody);
    let end = i + 1;
    // A heading line stands alone: it neither absorbs what follows it nor is
    // absorbed by what precedes it, gap or no gap.
    const startsWithHeadingLine = looksLikeStandaloneHeadingLine(line.text);
    while (
      !startsWithHeadingLine &&
      end < renderedLines.length &&
      !renderedLines[end].gapBefore &&
      !isTableCandidate(renderedLines[end]) &&
      !looksLikeStandaloneHeadingLine(renderedLines[end].text)
    ) {
      blockLines.push(renderedLines[end].text);
      tallerThanBody = tallerThanBody || Boolean(renderedLines[end].tallerThanBody);
      end++;
    }
    const blockText = joinWrappedLines(blockLines);
    if (line.gapBefore && rendered.length) rendered.push('');
    const level = headingLevelForBlock(blockText, {
      tallerThanBody,
      startsBlock: line.gapBefore || rendered.length === 0,
    });
    rendered.push(level === 3 ? `### ${blockText}` : level === 2 ? `## ${blockText}` : blockText);
    i = end;
  }

  return rendered.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Extract raw text from a PDF entirely in the browser using pdf.js.
 * Returns null if extraction fails, yields < 500 chars, or pdfjs-dist is unavailable.
 * This is the PREFERRED path for text-based PDFs: instant, no API cost, no timeout risk.
 */
/**
 * How long a pdf.js worker handshake may take before we stop waiting on it.
 *
 * WHY (INCIDENT-20260824, Fernando): every PDF read fetched the pdf.js worker
 * from unpkg.com — a third-party CDN — at RUNTIME, in the critical path, and
 * the only fallback fired on a THROW. When that fetch merely hung (cold
 * incognito cache, slow or filtered network), `getDocument(...).promise` never
 * settled: no error, no fallback, no timeout. The read sat at "reading
 * attachment" for 25 minutes with no progress — and no progress was itself the
 * clue, because the instrumented phases (rendering, transcribing) come AFTER
 * this point and never started.
 */
export const PDF_WORKER_HANDSHAKE_TIMEOUT_MS = 15_000;

/**
 * The pdf.js worker URL, preferring a copy served from OUR origin.
 *
 * The worker ships WITH the app under `static/`, which both deploy lanes
 * already serve `immutable` — instead of being fetched from someone else's
 * CDN on every cold load. The unpkg URL survives only as a last-resort
 * fallback for a build where the asset is missing.
 */
export function resolvePdfWorkerSources(version: string): string[] {
  const sources: string[] = [];
  if (version) {
    // Served from OUR origin. The copy lives at public/static/pdf-worker/ so
    // it lands under build/static/** — the path both deploy lanes already
    // serve `immutable` — and the version in the FILENAME is what makes that
    // safe: a pdfjs upgrade changes the name, so no browser can hold a stale
    // worker forever. pdfWorkerAsset.test.ts pins the checked-in copy to the
    // installed pdfjs-dist version so the two cannot desync.
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    sources.push(`${base}/static/pdf-worker/pdf.worker.${version}.min.mjs`);
  }
  // Last-resort network source, kept only for a build where the asset is
  // missing. It is never the ONLY source, and never unbounded.
  sources.push(`https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`);
  return sources;
}

/**
 * Open a PDF, never hanging on the worker.
 *
 * Tries each worker source with a bounded handshake, then falls back to
 * parsing on the main thread (`disableWorker`), which needs no network at all.
 * Slower for a large file — and unambiguously better than a spinner that never
 * moves. Shared by all three readers (text, figures, rasterisation) so the
 * guarantee cannot drift between them again.
 */
export async function openPdfDocument(
  pdfjsLib: any,
  arrayBuffer: ArrayBuffer,
  timeoutMs: number = PDF_WORKER_HANDSHAKE_TIMEOUT_MS,
): Promise<any> {
  const version = pdfjsLib?.version || '';
  // EVERY ATTEMPT NEEDS ITS OWN BYTES (INCIDENT-20260824-B). pdf.js TRANSFERS
  // the ArrayBuffer to its worker, which DETACHES it — after one
  // getDocument({ data }) the original is a zero-length husk. The first
  // version of this helper reused one buffer across the whole chain, so the
  // moment attempt 1 failed, every fallback — the CDN source AND the
  // main-thread parse — was handed an empty buffer and failed instantly.
  // The chain I shipped as "never hangs" could not actually fall back at all:
  // one failure at the top killed the read in about five seconds, and the
  // user was told the PDF had no text layer. Slicing is a copy, so each
  // attempt gets intact bytes.
  const attempts: Array<{ label: string; options: Record<string, unknown> }> = [
    ...resolvePdfWorkerSources(version).map((workerSrc) => ({ label: workerSrc, options: { workerSrc } })),
    // No worker reachable: parse on the main thread. No network required.
    { label: 'main-thread (disableWorker)', options: { disableWorker: true } },
  ];

  let lastError: unknown;
  for (const attempt of attempts) {
    const bytes = arrayBuffer.slice(0);
    let task: any;
    try {
      if (attempt.options.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = attempt.options.workerSrc as string;
      }
      task = pdfjsLib.getDocument({ data: bytes, ...(attempt.options.disableWorker ? { disableWorker: true } : {}) });
      // eslint-disable-next-line no-await-in-loop
      return await Promise.race([
        task.promise,
        new Promise((_resolve, reject) =>
          setTimeout(
            () => reject(new Error(`pdf.js open timed out after ${timeoutMs}ms (${attempt.label})`)),
            timeoutMs,
          )),
      ]);
    } catch (err) {
      lastError = err;
      console.warn(`[DocAnalyzer] pdf.js open failed via ${attempt.label} — trying the next source:`, err);
      // Release the worker/task this attempt created; its buffer is spent.
      try { await task?.destroy?.(); } catch { /* best effort */ }
    }
  }

  // Every source failed. Throw rather than return undefined: a caller that
  // silently treats "no document" as "no text layer" is how this surfaced to
  // the user as a false claim about their file.
  throw new Error(
    `pdf.js could not open the document via any source (last error: ${(lastError as any)?.message || lastError})`,
  );
}

export async function extractTextFromPdfClientSide(
  file: File,
  /**
   * Test seam (2026-08-25). Under jest the real pdfjs-dist ESM build cannot
   * be parsed ("Cannot use 'import.meta' outside a module"), and intercepting
   * this function's DYNAMIC import with jest.mock proved nondeterministic
   * under parallel battery load — the scannedTextLayerCoverage suite
   * intermittently received the real module, extraction threw, and three
   * unrelated-looking assertions failed. Tests pass their fake here instead;
   * production callers never set it.
   */
  pdfjsLibOverride?: unknown,
): Promise<string | null> {
  try {
    // Dynamically import to avoid SSR issues and keep the main bundle lean
    // @ts-ignore — pdfjs-dist types may not be in scope
    const pdfjsLib: any = pdfjsLibOverride ?? (await import('pdfjs-dist'));
    // Worker setup. The previous code pointed workerSrc at
    // `//cdnjs.cloudflare.com/.../pdf.worker.min.js`, but pdfjs-dist v5 ships
    // the worker as `pdf.worker.min.mjs` (note the .mjs extension) and that
    // exact version is frequently not present on cdnjs — so the worker 404'd,
    // text extraction silently returned nothing, and EVERY text PDF fell through
    // to the slow vision tier and hit the 75s analysis timeout (Point 3 doc-reader
    // timeout bug). Fix: use the version-correct .mjs worker URL, and if the
    // worker can't load, fall back to running pdf.js on the main thread
    // (disableWorker) so extraction still succeeds instead of silently failing.
    const arrayBuffer = await file.arrayBuffer();
    // Bounded worker handshake with a main-thread fallback — never hangs
    // (INCIDENT-20260824; see openPdfDocument).
    const pdf = await openPdfDocument(pdfjsLib, arrayBuffer);
    const totalPages = pdf.numPages;
    const textParts: string[] = [];
    const pageBodies: string[] = [];
    const pageNumbers: number[] = [];

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      // Layout-preserving: lines, paragraphs and headings survive (see
      // buildPageTextFromItems). A flat join here is what cost the 45-page
      // report its headings, its paragraph shape and its page breaks.
      const pageText = buildPageTextFromItems(content.items as PdfTextItem[]);
      pageBodies.push(pageText);
      pageNumbers.push(pageNum);
    }

    // Running headers and page footers repeat at the page edges; they are
    // furniture, not prose, and translating them 200 times is pure waste.
    const cleanedBodies = stripRepeatedPageFurniture(pageBodies);
    cleanedBodies.forEach((body, idx) => {
      if (body.trim()) {
        textParts.push(`--- Page ${pageNumbers[idx]} ---\n${body}`);
      }
    });

    const fullText = textParts.join('\n\n').trim();

    // ── Is this text layer good enough to SKIP OCR? (INCIDENT-20260824-D) ──
    //
    // This gate used to be one number: 500 characters for the whole document.
    // It is the gate that decides whether the expensive scanned-page pass runs
    // at all, and it was blind to how many pages it was judging.
    //
    // Fernando's 30-page scanned clinical record has a thin text layer on its
    // cover page — hospital header, patient line, the first two numbered
    // items. That cleared 500 characters, so the document was declared read,
    // OCR never ran, and the model received page 1 of 30 as though it were the
    // whole record. It said so accurately ("solo pude leer el encabezado y los
    // dos primeros puntos de la página 1"); downstream simply carried on. The
    // same fragment reached a translation run and shipped as a 376-character
    // "translation" of 57,000 characters of source.
    //
    // Twenty characters per page passed a bar meant for a one-page letter.
    // Coverage — how many pages actually carry text — is the real question,
    // and a flat total can never answer it.
    //
    // The two failure directions are NOT symmetric, so this leans toward OCR:
    //   • Text PDF wrongly sent to OCR  → slower, costs a little more, and the
    //     user still gets their whole document.
    //   • Scan wrongly kept on the fast path → the user silently receives a
    //     thirtieth of their document, and everything downstream believes it.
    const pagesWithText = cleanedBodies.filter(
      (body) => body.trim().length >= MIN_TEXT_LAYER_PAGE_CHARS,
    ).length;
    const coverage = totalPages > 0 ? pagesWithText / totalPages : 0;

    if (fullText.length < 500) {
      console.log(`[DocAnalyzer] PDF client-side extraction yielded only ${fullText.length} chars — falling back to API`);
      return null;
    }
    if (totalPages > 1 && coverage < MIN_TEXT_LAYER_PAGE_COVERAGE) {
      console.log(
        `[DocAnalyzer] PDF text layer covers only ${pagesWithText}/${totalPages} pages ` +
        `(${Math.round(coverage * 100)}% < ${Math.round(MIN_TEXT_LAYER_PAGE_COVERAGE * 100)}%) — ` +
        `treating as scanned and falling back to transcription`,
      );
      return null;
    }

    console.log(`[DocAnalyzer] PDF client-side extraction succeeded: ${totalPages} pages, ${fullText.length} chars, text on ${pagesWithText}/${totalPages} pages (no API call needed)`);
    return fullText;
  } catch (err) {
    console.warn('[DocAnalyzer] PDF client-side extraction failed, falling back to API:', err);
    return null;
  }
}

// ============================================================================
// Scanned-PDF rasterisation (OCR input) — 2026-08-13
// ============================================================================

/**
 * How many pages of a scanned PDF are rasterised for transcription.
 *
 * Each page becomes one image in a single vision request, so this bounds both
 * the request size and the cost. 30 covers the great majority of scanned
 * filings; beyond it the user is told plainly how much was read rather than
 * being handed a silently partial document.
 */
/** Embedded figures pulled out of a source PDF, keyed to the page they sat on. */
export interface SourcePageImage {
  /** 1-based page in the SOURCE document. */
  pageNumber: number;
  /** Base64 PNG (no data: prefix). */
  base64: string;
  widthPx: number;
  heightPx: number;
  /**
   * Where the figure sits in its page's text flow, as the FRACTION of that
   * page's characters appearing above it (0 = top of page, 1 = below all text).
   *
   * WHY A RATIO (2026-08-13): figures used to be drawn after ALL of their
   * page's text, which is wrong for most of them — measured on the 45-page
   * report, figures sit at 0.07, 0.50 and 0.92 through their pages. Dumping
   * them at the end pushed each one past the bottom margin onto a page of its
   * own: 20 near-empty pages averaging 416pt of 700pt unused, and 88 output
   * pages for a 45-page source.
   *
   * A ratio rather than a y-coordinate because the deliverable reflows —
   * translated English is not the same length as the Spanish it came from, so
   * an absolute position would be meaningless. It also keeps figure placement
   * entirely out of the translated text, which is what makes it model-proof:
   * unlike a marker or a placeholder token, there is nothing here for the
   * model to reflow, translate or drop.
   */
  afterCharRatio?: number;
}

/** Bound on figures carried into the deliverable — 200 pages of charts is a lot of bytes. */
export const MAX_SOURCE_IMAGES = 120;

/** Ignore decorative slivers: rules, bullets, logos in a running header. */
const MIN_FIGURE_PX = 80;

/**
 * An image covering at least this share of its page IS the page, not a figure
 * on it.
 *
 * WHY (2026-08-21, the scanned 30-page clinical record): a scanned PDF has no
 * text and no artwork — each page is ONE full-page photograph. Figure
 * extraction dutifully collected all 30 of them and the builders embedded them
 * in the deliverable, so a 30-page source produced a 60-page output in which
 * every second page was the untranslated Spanish original. Carrying the source
 * back into its own translation defeats the entire point of translating it.
 */
export const FULL_PAGE_IMAGE_COVERAGE = 0.8;

/**
 * Pull the embedded images (charts, photographs, diagrams) out of a PDF,
 * tagged with the page they appeared on.
 *
 * WHY (2026-08-13 formatting feedback): the translated 45-page financial
 * report contained none of its 35 figures, because the pipeline is text-only —
 * extraction returns a string and buildFormattedPdf has no image support at
 * all. Keying each figure to its source page lets the generator put it back on
 * the corresponding page of the deliverable, now that source page breaks are
 * preserved.
 *
 * FAIL-SAFE: every failure path returns what has been collected so far (often
 * nothing) rather than throwing. Missing figures is the status quo; a crash in
 * document generation would be a regression.
 */
/** 2D affine matrix multiply, pdf.js operand order. */
function multiplyMatrix(m: number[], n: number[]): number[] {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

/**
 * Fraction of a page's characters that sit ABOVE a y coordinate.
 *
 * PDF space grows upward, so "above" is a LARGER y. Pure, so the ratio that
 * decides where every figure lands is testable without a PDF.
 */
export function charRatioAboveY(
  items: PdfTextItem[],
  y: number,
): number {
  let total = 0;
  let above = 0;
  for (const it of items || []) {
    const len = (it?.str || '').length;
    if (!len) continue;
    total += len;
    if (Array.isArray(it.transform) && it.transform.length >= 6 && Number(it.transform[5]) > y) {
      above += len;
    }
  }
  if (total === 0) return 1;
  return Math.min(1, Math.max(0, above / total));
}

/**
 * Wall-clock budget for pulling figures out of ONE source document.
 *
 * WHY (2026-08-20): figure extraction had no time bound at all. On the
 * 200-page manual the turn stopped dead at "collecting figures from the
 * source" and never moved again. A bounded, honest partial (the figures we
 * managed to collect) always beats an unbounded wait — the translation itself
 * is already banked by this point, so the only thing at risk here is artwork.
 */
export const FIGURE_EXTRACTION_BUDGET_MS = 90_000;

/**
 * Bound on waiting for ONE image object to come back from the pdf.js worker.
 *
 * pdf.js resolves an object by invoking a callback. If the object never
 * resolves the callback is simply never invoked — there is no error, no
 * rejection, and the await never settles. That is a permanent hang, so every
 * such wait needs a deadline.
 */
export const FIGURE_OBJECT_TIMEOUT_MS = 5_000;

export interface FigureExtractionProgress {
  /** 1-based page just finished. */
  pageNumber: number;
  totalPages: number;
  /** Figures collected so far. */
  figures: number;
}

export interface FigureExtractionOptions {
  onProgress?: (info: FigureExtractionProgress) => void;
  budgetMs?: number;
  objectTimeoutMs?: number;
  signal?: AbortSignal;
}

/**
 * Resolve one image object from the CORRECT pdf.js pool, with a deadline.
 *
 * ROOT CAUSE OF THE 2026-08-20 HANG. pdf.js keeps images in two pools and
 * dispatches on the object id: ids beginning `g_` are GLOBAL (the same image
 * painted on more than one page — a running-header logo, a repeated diagram)
 * and live on `commonObjs`; everything else is page-local and lives on `objs`.
 * pdf.js does this itself in its own operator executor:
 *
 *   const objsPool = depObjId.startsWith("g_") ? commonObjs : objs;
 *
 * We only ever asked `page.objs`. Asking the wrong pool for a global image
 * does not throw and does not return null — it REGISTERS A CALLBACK THAT IS
 * NEVER INVOKED, so the surrounding `await` blocks forever. A 200-page
 * technical manual with a logo in its running header hits this on page 1 and
 * the whole turn stops there.
 *
 * Two fixes in one: ask the right pool, and never wait unbounded on either.
 */
export async function resolvePdfImageObject(
  page: { objs?: any; commonObjs?: any },
  objId: string,
  timeoutMs: number = FIGURE_OBJECT_TIMEOUT_MS,
): Promise<any> {
  const pool = objId.startsWith('g_') ? page?.commonObjs : page?.objs;
  if (!pool) return null;

  // Fast path: already resolved, so no callback and no waiting at all.
  try {
    if (typeof pool.has === 'function' && pool.has(objId)) {
      return pool.get(objId);
    }
  } catch {
    return null;
  }

  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (value: any) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      resolve(value);
    };
    // Armed BEFORE the get, but tolerant of a synchronous callback.
    timer = setTimeout(() => finish(null), timeoutMs);
    try {
      pool.get(objId, (o: unknown) => finish(o));
    } catch {
      finish(null);
    }
  });
}

/** Hand the frame back to the browser so progress actually paints. */
function yieldToUi(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export async function extractPdfImagesClientSide(
  file: File,
  maxImages: number = MAX_SOURCE_IMAGES,
  options: FigureExtractionOptions = {},
): Promise<SourcePageImage[]> {
  const images: SourcePageImage[] = [];
  const budgetMs = options.budgetMs ?? FIGURE_EXTRACTION_BUDGET_MS;
  const objectTimeoutMs = options.objectTimeoutMs ?? FIGURE_OBJECT_TIMEOUT_MS;
  const startedAt = Date.now();
  const outOfTime = () => Date.now() - startedAt > budgetMs;
  const aborted = () => options.signal?.aborted === true;
  try {
    // @ts-ignore — pdfjs-dist types may not be in scope
    const pdfjsLib = await import('pdfjs-dist');
    const arrayBuffer = await file.arrayBuffer();
    // Bounded worker handshake with a main-thread fallback (INCIDENT-20260824).
    const pdf = await openPdfDocument(pdfjsLib, arrayBuffer);

    const OPS = (pdfjsLib as any).OPS;
    for (let pageNumber = 1; pageNumber <= pdf.numPages && images.length < maxImages; pageNumber++) {
      if (aborted()) break;
      if (outOfTime()) {
        console.warn(
          `[DocAnalyzer] Figure extraction budget (${budgetMs}ms) reached at page ${pageNumber} of ${pdf.numPages}; keeping ${images.length} figure(s)`,
        );
        break;
      }
      try {
        const page = await pdf.getPage(pageNumber);
        const ops = await page.getOperatorList();
        const view: number[] = Array.isArray(page.view) ? page.view : [];
        const pageArea = view.length >= 4
          ? Math.abs((view[2] - view[0]) * (view[3] - view[1]))
          : 0;
        // The page's text, so each figure can be placed at its real position in
        // the flow rather than after everything (see afterCharRatio).
        let pageTextItems: PdfTextItem[] = [];
        try {
          pageTextItems = (await page.getTextContent()).items as PdfTextItem[];
        } catch {
          // Placement degrades to end-of-page; the figure is still carried.
        }
        // Current transformation matrix, tracked across save/restore/transform
        // so a paint op's matrix gives the figure's position AND size on the
        // page. pdf.js hands the operator list out flat, so this is the only
        // way to know where an image actually sat.
        let ctm: number[] = [1, 0, 0, 1, 0, 0];
        const ctmStack: number[][] = [];
        for (let i = 0; i < ops.fnArray.length && images.length < maxImages; i++) {
          const fn = ops.fnArray[i];
          if (fn === OPS.save) {
            ctmStack.push([...ctm]);
            continue;
          }
          if (fn === OPS.restore) {
            ctm = ctmStack.pop() || [1, 0, 0, 1, 0, 0];
            continue;
          }
          if (fn === OPS.transform) {
            const args = ops.argsArray[i];
            if (Array.isArray(args) && args.length >= 6) ctm = multiplyMatrix(ctm, args);
            continue;
          }
          if (fn !== OPS.paintImageXObject && fn !== OPS.paintJpegXObject) continue;
          if (aborted() || outOfTime()) break;
          const objId = ops.argsArray[i]?.[0];
          if (typeof objId !== 'string') continue;
          // The CTM's scale IS the painted size on the page, so a page-sized
          // paint is the page itself — a scan, a background, a full-bleed
          // plate — and must never be carried into the deliverable.
          if (pageArea > 0) {
            const paintedArea = Math.abs(ctm[0] * ctm[3]) + Math.abs(ctm[1] * ctm[2]);
            if (paintedArea / pageArea >= FULL_PAGE_IMAGE_COVERAGE) continue;
          }
          // The image fills the unit square under the CTM, so its top edge is
          // the translation plus the vertical scale.
          const figureTopY = ctm[5] + Math.abs(ctm[3]);
          const afterCharRatio = charRatioAboveY(pageTextItems, figureTopY);

          const img: any = await resolvePdfImageObject(page, objId, objectTimeoutMs);
          if (!img || !img.width || !img.height) continue;
          if (img.width < MIN_FIGURE_PX || img.height < MIN_FIGURE_PX) continue;

          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;

          if (img.bitmap) {
            ctx.drawImage(img.bitmap, 0, 0);
          } else if (img.data) {
            // pdf.js hands back RGB or RGBA; ImageData needs RGBA.
            const rgba = new Uint8ClampedArray(img.width * img.height * 4);
            const src = img.data as Uint8Array;
            const channels = src.length / (img.width * img.height);
            for (let p = 0, s = 0, d = 0; p < img.width * img.height; p++, s += channels, d += 4) {
              rgba[d] = src[s];
              rgba[d + 1] = src[s + (channels > 1 ? 1 : 0)];
              rgba[d + 2] = src[s + (channels > 2 ? 2 : 0)];
              rgba[d + 3] = channels === 4 ? src[s + 3] : 255;
            }
            ctx.putImageData(new ImageData(rgba, img.width, img.height), 0, 0);
          } else {
            continue;
          }

          const dataUrl = canvas.toDataURL('image/png');
          const comma = dataUrl.indexOf(',');
          if (comma > 0) {
            images.push({
              pageNumber,
              base64: dataUrl.slice(comma + 1),
              widthPx: img.width,
              heightPx: img.height,
              afterCharRatio,
            });
          }
          canvas.width = 0;
          canvas.height = 0;
        }
        // A 200-page document holds a lot of decoded page data otherwise.
        try {
          page.cleanup();
        } catch { /* best effort */ }
      } catch (pageErr) {
        console.warn(`[DocAnalyzer] Figure extraction failed on page ${pageNumber}:`, pageErr);
      }
      // Report AFTER the page, including pages that failed, so the caller's
      // progress reflects real position in the document and the liveness
      // heartbeat keeps beating through a long extraction.
      try {
        options.onProgress?.({ pageNumber, totalPages: pdf.numPages, figures: images.length });
      } catch { /* a progress paint must never break extraction */ }
      await yieldToUi();
    }
    console.log(`[DocAnalyzer] Extracted ${images.length} figure(s) from ${file.name}`);
  } catch (err) {
    console.warn('[DocAnalyzer] Figure extraction unavailable:', err);
  }
  return images;
}

/**
 * Cap on the text handed to the SUMMARY prompt.
 *
 * A long PDF (e.g. the 20-page / ~85K-char research report) produces an
 * enormous prompt that makes the model summary run very long / stall (the
 * "still working" hang). ~48K chars is ample for an accurate summary while
 * keeping the generation prompt bounded and responsive.
 *
 * This bounds the SUMMARY only — the complete text is banked in `fullTexts`
 * so whole-document tools (translation) never inherit the cap.
 */
/**
 * A page needs at least this much text to count as "has a text layer".
 * A scanned page's stray OCR artefacts and a cover-page header both land
 * well under it; a real page of prose clears it several times over.
 */
export const MIN_TEXT_LAYER_PAGE_CHARS = 100;

/**
 * And at least this fraction of a multi-page PDF's pages must clear that bar
 * before we trust the text layer and skip transcription.
 *
 * Set at 60% rather than something near-total on purpose: real documents
 * legitimately carry near-empty pages — exhibit dividers, signature pages,
 * section breaks, intentionally blank verso pages. A scan sits nowhere near
 * this line (Fernando's record: 1 page in 30, 3%), so the boundary is wide on
 * both sides and does not need to be finely tuned.
 */
export const MIN_TEXT_LAYER_PAGE_COVERAGE = 0.6;

export const MAX_EXTRACTED_CHARS = 48_000;

export const OCR_RASTERIZE_MAX_PAGES = 30;

/** Target raster width in px. Below ~1200 small print stops being legible. */
export const OCR_TARGET_WIDTH_PX = 1500;

/**
 * Scale factor to render a page at ~OCR_TARGET_WIDTH_PX.
 *
 * Clamped: never upscale a large page past 2x (wasted bytes, no extra detail)
 * and never render below 1x (illegible). Pure so it can be tested without a
 * canvas.
 */
export function computeRasterScale(
  viewportWidthAtScale1: number,
  targetWidth: number = OCR_TARGET_WIDTH_PX,
): number {
  if (!Number.isFinite(viewportWidthAtScale1) || viewportWidthAtScale1 <= 0) return 1;
  const raw = targetWidth / viewportWidthAtScale1;
  return Math.min(2, Math.max(1, raw));
}

export interface RasterizedPage {
  pageNumber: number;
  base64: string;
  mediaType: string;
}

/**
 * Render the pages of a scanned PDF to JPEG images for transcription.
 *
 * WHY (INCIDENT-20260812): a 30-page image-only PDF was sent to the model as a
 * single base64 `document` block. anthropicProxy converts that correctly to the
 * OpenAI `file`/`file_data` shape, but the provider did not read it — the model
 * replied "No document was attached or accessible", which the pipeline then
 * treated as content. Images (`image` -> `image_url`) are supported far more
 * broadly than PDF file blocks, so rendering pages ourselves removes the
 * dependency on provider-side PDF parsing entirely.
 *
 * Returns null when rasterisation is unavailable (no canvas, pdf.js failure) so
 * the caller can fall back rather than crash.
 */
/**
 * What a long read is doing right now.
 *
 * WHY (2026-08-21, third occurrence of this class): reading a scanned
 * document renders every page to an image and then makes a vision call per
 * batch of four. For a 30-page record that is genuinely minutes of work — and
 * it reported NOTHING. The spinner said "reading attachment" and sat there,
 * so a working read and a hung one looked identical, to Miles and to me. The
 * frozen 100% and the figure hang were the same defect in different clothes.
 *
 * A long step that says nothing is indistinguishable from a broken one.
 */
export interface TranscriptionProgress {
  phase: 'rendering' | 'transcribing';
  /** Pages rendered, or pages whose transcription has come back. */
  done: number;
  total: number;
}

/**
 * Wall-clock budget for transcribing ONE scanned document.
 *
 * Eight vision calls can stall upstream with no error. Returning the pages we
 * did read, and saying how many, beats waiting forever — the caller already
 * knows how to report partial coverage honestly.
 */
export const OCR_TRANSCRIPTION_BUDGET_MS = 8 * 60 * 1000;

/**
 * pdf.js's AnnotationMode.ENABLE: every annotation (ink, stamps) and every form field's appearance is
 * painted onto the canvas. It is pdf.js 5's default today, named here because ENABLE_FORMS, the
 * viewer's mode, leaves text fields, boxes and choices to an HTML form layer that a page turned into a
 * picture never has: a date typed into a field would never reach the vision model (issue
 * tri_mv2y073s_e5a863260c).
 */
export const PDFJS_ANNOTATION_MODE_ENABLE = 1;

export async function rasterizePdfPagesClientSide(
  file: File,
  maxPages: number = OCR_RASTERIZE_MAX_PAGES,
  onProgress?: (info: TranscriptionProgress) => void,
  /** Test seam, as extractTextFromPdfClientSide's: production callers never set it. */
  pdfjsLibOverride?: unknown,
): Promise<{ pages: RasterizedPage[]; totalPages: number } | null> {
  try {
    // @ts-ignore — pdfjs-dist types may not be in scope
    const pdfjsLib: any = pdfjsLibOverride ?? (await import('pdfjs-dist'));
    const annotationMode = typeof pdfjsLib?.AnnotationMode?.ENABLE === 'number'
      ? pdfjsLib.AnnotationMode.ENABLE
      : PDFJS_ANNOTATION_MODE_ENABLE;
    const arrayBuffer = await file.arrayBuffer();
    // Bounded worker handshake with a main-thread fallback (INCIDENT-20260824).
    const pdf = await openPdfDocument(pdfjsLib, arrayBuffer);

    const totalPages = pdf.numPages as number;
    const pageCount = Math.min(totalPages, Math.max(1, maxPages));
    const pages: RasterizedPage[] = [];

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = computeRasterScale(baseViewport.width);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const canvasContext = canvas.getContext('2d');
      if (!canvasContext) {
        console.warn('[DocAnalyzer] No 2D canvas context — cannot rasterise scanned PDF');
        return null;
      }
      // pdfjs v5 wants the canvas alongside its context. The picture carries what a person added:
      // ink, stamps, and every form field with its value.
      await page.render({ canvas, canvasContext, viewport, annotationMode }).promise;

      // JPEG at 0.8: a scan is a photograph of paper, and PNG would multiply
      // the request size for no legibility gain.
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      const commaIndex = dataUrl.indexOf(',');
      if (commaIndex < 0) {
        console.warn(`[DocAnalyzer] Rasterise produced no data for page ${pageNumber}`);
        return null;
      }
      pages.push({
        pageNumber,
        base64: dataUrl.slice(commaIndex + 1),
        mediaType: 'image/jpeg',
      });
      // Free the bitmap immediately; 30 full-page canvases at once is a lot of
      // memory on a modest laptop.
      canvas.width = 0;
      canvas.height = 0;
      try {
        onProgress?.({ phase: 'rendering', done: pages.length, total: pageCount });
      } catch { /* a progress paint must never break the read */ }
      // Rendering a page is main-thread work; hand the frame back so the
      // progress the user is watching actually paints.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    console.log(
      `[DocAnalyzer] Rasterised ${pages.length}/${totalPages} page(s) of ${file.name} for transcription`,
    );
    return { pages, totalPages };
  } catch (err) {
    console.warn('[DocAnalyzer] PDF rasterisation failed:', err);
    return null;
  }
}

// ============================================================================
// Scanned-PDF Transcription (OCR)
// ============================================================================
//
// WHY THIS EXISTS AS ITS OWN PASS (2026-08-13)
//
// Rasterising the pages was only half the job. The page images were appended
// to the ANALYSIS request, so the transcription came back inside `analysis` —
// the model's prose ABOUT the document — while `extractedFullText` stayed
// empty. The translate tool requires raw text and refuses the summary
// (INCIDENT-20260812: translating the summary is how a 200-page expediente
// became a 10-page description of itself). So the scanned file was rasterised,
// transcribed, and then correctly refused: "I couldn't extract any text".
//
// Transcription is therefore its own pass that produces RAW TEXT, which then
// joins the ordinary text-layer path. Every downstream stage — the 48K summary
// cap, fullTexts, extractedFullText, translation segmentation, and the source
// page breaks from #773 — works unchanged, because by then a scanned PDF is
// indistinguishable from a text PDF.
//
// It is BATCHED because one call cannot hold 30 pages: the analysis request
// caps output at 8192 tokens (~30K chars) and a 30-page document runs 60-90K
// chars, so a single-call transcription would be silently truncated mid-page.

/**
 * Pages per transcription call. Four pages of dense text is ~10K output
 * tokens, comfortably inside OCR_BATCH_MAX_TOKENS with room for a page that
 * runs long. Larger batches risk truncation; smaller ones multiply requests.
 */
export const OCR_PAGES_PER_BATCH = 4;

/** Concurrent transcription calls. Matches the translator's worker pool. */
export const OCR_BATCH_CONCURRENCY = 3;

export const OCR_BATCH_MAX_TOKENS = 16_000;

/**
 * Below this many characters per page the "transcription" is not one — the
 * model answered about the images instead of transcribing them.
 */
export const OCR_MIN_CHARS_PER_PAGE = 12;

const OCR_TRANSCRIPTION_PROMPT = `
You are a transcription engine for scanned documents. You receive page images and return their text.

RULES:
- Transcribe VERBATIM in the ORIGINAL language. Never translate.
- Never summarise, describe, comment, or add preamble. Output only the transcription.
- Preserve line breaks, bullets, numbering and indentation.
- Tables: preserve structure with | delimiters.
- Mark unclear words [?] and illegible passages [illegible].
- Begin every page with its own marker line, exactly: --- Page N ---
`.trim();

/**
 * A transcription that says the model could not see the pages, rather than
 * their contents. Accepting one of these as document text is how analyzer
 * prose reaches a client as their "translation".
 */
const OCR_REFUSAL_SHAPED =
  /\b(?:i (?:cannot|can't|am unable to|do not see|don't see)|no (?:documents?|images?|pages?|attachments?) (?:were |was |are |is )?(?:attached|provided|visible|accessible|included)|unable to (?:read|see|access|process|view)|there (?:are|is) no (?:images?|documents?|pages?))\b/i;

/** Split rasterised pages into transcription batches, preserving order. */
export function chunkPagesForOcr<T>(pages: T[], size: number = OCR_PAGES_PER_BATCH): T[][] {
  const batchSize = Math.max(1, Math.floor(size));
  const batches: T[][] = [];
  for (let i = 0; i < pages.length; i += batchSize) {
    batches.push(pages.slice(i, i + batchSize));
  }
  return batches;
}

/**
 * The per-batch instruction. The page numbers are stated explicitly because
 * the model sees only a handful of images and cannot know it is looking at
 * pages 17-20 — and a marker with the wrong number puts every downstream page
 * break in the wrong place.
 */
export function buildOcrBatchInstruction(
  fileName: string,
  pageNumbers: number[],
  totalPages: number,
): string {
  const list = pageNumbers.join(', ');
  return [
    `"${fileName}" is a scanned PDF with no text layer.`,
    `The ${pageNumbers.length} image(s) below are page(s) ${list} of ${totalPages}, in order.`,
    '',
    'Transcribe every page VERBATIM in its original language.',
    `Precede each page's text with its marker line, using exactly these numbers: ${pageNumbers
      .map((n) => `--- Page ${n} ---`)
      .join(', ')}.`,
    'If a page is blank or wholly illegible, still emit its marker line, followed by [illegible].',
    'Output nothing except the marker lines and the transcribed text.',
  ].join('\n');
}

/** Multiline form of the marker the transcription must emit. */
const SOURCE_PAGE_MARKER_LINE = /^\s*-{2,}\s*[\p{L}]+\s+\d+\s*-{2,}\s*$/mu;

/**
 * Why a batch result cannot be used as document text, or null if it can.
 */
export function unusableTranscriptionReason(text: string, pageCount: number): string | null {
  const trimmed = (text || '').trim();
  if (!trimmed) return 'empty';
  if (OCR_REFUSAL_SHAPED.test(trimmed.slice(0, 400))) return 'model reported it could not read the pages';
  if (!SOURCE_PAGE_MARKER_LINE.test(trimmed)) return 'no page markers';
  const bodyChars = trimmed.replace(/^\s*-{2,}\s*[\p{L}]+\s+\d+\s*-{2,}\s*$/gmu, '').trim().length;
  if (bodyChars < OCR_MIN_CHARS_PER_PAGE * pageCount) return `only ${bodyChars} chars for ${pageCount} page(s)`;
  return null;
}

export interface ScannedPdfTranscript {
  /** Raw transcribed text, page markers included. */
  text: string;
  pagesTranscribed: number;
  totalPages: number;
  /** Pages whose batch failed — represented by a gap note in `text`. */
  failedPages: number[];
}

/**
 * Stitch batch results back into one document.
 *
 * A failed batch becomes a VISIBLE gap rather than a silent omission: the
 * pages keep their markers so the deliverable still paginates correctly, and
 * the client can see exactly which pages are missing. Dropping them quietly
 * would produce a shorter document that looks complete — the failure mode this
 * whole incident chain is made of.
 */
export function assembleOcrTranscript(
  batchPageNumbers: number[][],
  batchTexts: Array<string | null>,
  totalPages: number,
): ScannedPdfTranscript {
  const parts: string[] = [];
  const failedPages: number[] = [];
  let pagesTranscribed = 0;

  batchPageNumbers.forEach((pageNumbers, index) => {
    const text = batchTexts[index];
    if (text && text.trim()) {
      parts.push(text.trim());
      pagesTranscribed += pageNumbers.length;
      return;
    }
    for (const pageNumber of pageNumbers) {
      failedPages.push(pageNumber);
      parts.push(`--- Page ${pageNumber} ---\n[This page could not be transcribed.]`);
    }
  });

  const covered = batchPageNumbers.reduce((sum, batch) => sum + batch.length, 0);
  if (totalPages > covered) {
    parts.push(
      `[Pages ${covered + 1}-${totalPages} of ${totalPages} were not transcribed: ` +
      `this document exceeds the ${OCR_RASTERIZE_MAX_PAGES}-page scan limit.]`,
    );
  }

  return {
    text: parts.join('\n\n'),
    pagesTranscribed,
    totalPages,
    failedPages,
  };
}

/**
 * Transcribe a scanned PDF to raw text.
 *
 * Returns null when the file could not be rasterised or no batch produced
 * usable text, so the caller falls back to the previous behaviour rather than
 * inventing content.
 */
export async function transcribeScannedPdf(
  file: File,
  signal?: AbortSignal,
  onProgress?: (info: TranscriptionProgress) => void,
): Promise<ScannedPdfTranscript | null> {
  const startedAt = Date.now();
  const rasterised = await rasterizePdfPagesClientSide(file, OCR_RASTERIZE_MAX_PAGES, onProgress);
  if (!rasterised || rasterised.pages.length === 0) return null;

  const { pages, totalPages } = rasterised;
  const batches = chunkPagesForOcr(pages);
  const batchTexts: Array<string | null> = new Array(batches.length).fill(null);
  console.log(
    `[DocAnalyzer] [OCR] Transcribing ${pages.length}/${totalPages} page(s) of ${file.name} ` +
    `in ${batches.length} batch(es) of up to ${OCR_PAGES_PER_BATCH}`,
  );

  let cursor = 0;
  let pagesDone = 0;
  const report = () => {
    try {
      onProgress?.({ phase: 'transcribing', done: pagesDone, total: pages.length });
    } catch { /* a progress paint must never break the read */ }
  };
  report();
  const worker = async (): Promise<void> => {
    for (;;) {
      const index = cursor++;
      if (index >= batches.length) return;
      if (signal?.aborted) return;
      if (Date.now() - startedAt > OCR_TRANSCRIPTION_BUDGET_MS) {
        console.warn(
          `[DocAnalyzer] [OCR] Budget (${OCR_TRANSCRIPTION_BUDGET_MS}ms) reached with ` +
          `${pagesDone}/${pages.length} page(s) transcribed — returning what was read`,
        );
        return;
      }
      const batch = batches[index];
      const pageNumbers = batch.map((p) => p.pageNumber);
      try {
        const ocrMessages = [
          { role: 'system' as const, content: OCR_TRANSCRIPTION_PROMPT },
          { role: 'user' as const, content: buildOcrBatchInstruction(file.name, pageNumbers, totalPages) },
        ];
        const ocrAttachments = batch.map((p) => ({
          type: 'image_url' as const,
          url: `data:${p.mediaType};base64,${p.base64}`,
          mimeType: p.mediaType,
          filename: `${file.name}-page-${p.pageNumber}.jpg`,
        }));
        // D14 slice 5c: the route name is the routing input — the server
        // stamps the document-analysis ladder + effort. Riding the streaming
        // transport also buys keep-alive tolerance on heavy vision batches.
        // (Slice 7, 2026-09-15: the deploy-skew fallback retired — a batch
        // failure is a gap, handled by the catch below.)
        const result = await runTaskStream({
          route: 'document-analysis',
          meterSummary: DOC_ANALYSIS_METER_SUMMARY,
          messages: ocrMessages,
          attachments: ocrAttachments,
          maxTokens: OCR_BATCH_MAX_TOKENS,
          temperature: 0,
          signal,
        });
        const reason = unusableTranscriptionReason(result.text, batch.length);
        if (reason) {
          console.warn(`[DocAnalyzer] [OCR] Pages ${pageNumbers.join(',')} unusable: ${reason}`);
          continue;  // the finally block still advances the counter
        }
        batchTexts[index] = result.text.trim();
        console.log(`[DocAnalyzer] [OCR] Pages ${pageNumbers.join(',')} transcribed (${result.text.trim().length} chars)`);
      } catch (err) {
        // One bad batch is a gap; it must never cost the whole document.
        console.warn(`[DocAnalyzer] [OCR] Pages ${pageNumbers.join(',')} failed:`, err);
      } finally {
        // Counted whatever happened: the user is watching position in the
        // document, not a success rate.
        pagesDone += batch.length;
        report();
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(OCR_BATCH_CONCURRENCY, batches.length) }, worker),
  );

  const transcript = assembleOcrTranscript(
    batches.map((batch) => batch.map((p) => p.pageNumber)),
    batchTexts,
    totalPages,
  );
  if (transcript.pagesTranscribed === 0) {
    console.warn(`[DocAnalyzer] [OCR] No page of ${file.name} could be transcribed`);
    return null;
  }
  console.log(
    `[DocAnalyzer] [OCR] ${file.name}: ${transcript.pagesTranscribed}/${totalPages} page(s), ` +
    `${transcript.text.length} chars` +
    (transcript.failedPages.length ? `, gaps on page(s) ${transcript.failedPages.join(',')}` : ''),
  );
  return transcript;
}

// ============================================================================
// Main Analysis Function
// ============================================================================

/**
 * Analyze documents using Claude's vision capabilities and text extraction
 */
export async function analyzeDocumentsWithClaude(params: DocumentAnalysisParams): Promise<DocumentAnalysisResult> {
  const { files, userQuery, signal, onProgress } = params;

  // Note: API key is handled server-side by Firebase proxy
  console.log(`[DocAnalyzer] Route document-analysis (rung-0 display: ${CLAUDE_VISION_MODEL})`);

  if (!files || files.length === 0) {
    console.log('[DocAnalyzer] No files provided, returning empty result');
    return {
      analysis: '',
      filesAnalyzed: 0,
      skippedFiles: [],
    };
  }

  console.log(`[DocAnalyzer] Starting analysis of ${files.length} file(s)...`);
  console.log(`[DocAnalyzer] User query: ${userQuery?.substring(0, 100) || 'none'}...`);
  
  // Build message content with files
  const { content, skipped, textExtracted, hasPdfFiles, fullTexts, ocrFiles } = await buildMessageContent(files, userQuery, signal, onProgress);

  // Complete, untruncated source text for whole-document tools. Multiple files
  // are separated by a labelled header so a translator keeps them distinct.
  const extractedFullText = fullTexts.length === 1
    ? fullTexts[0].text
    : fullTexts.map(({ name, text }) => `--- Document: ${name} ---\n${text}`).join('\n\n');
  const fullTextSource: 'text-layer' | 'ocr' | 'mixed' | undefined =
    ocrFiles.length === 0
      ? 'text-layer'
      : ocrFiles.length === fullTexts.length
        ? 'ocr'
        : 'mixed';
  if (extractedFullText) {
    console.log(`[DocAnalyzer] Retained ${extractedFullText.length} chars of untruncated text from ${fullTexts.length} file(s) for whole-document tools (source: ${fullTextSource})`);
  }
  
  // Check if we have any files to analyze
  // Content includes query text + instruction text, so we need at least 3 items for actual content
  const hasVisionContent = content.some((c: unknown) => {
    const item = c as { type?: string };
    return item.type === 'image' || item.type === 'document';
  });
  const hasTextContent = textExtracted.length > 0;
  
  if (!hasVisionContent && !hasTextContent) {
    console.warn('[DocAnalyzer] No supported files to analyze');
    return {
      analysis: `[No supported documents to analyze. Supported formats: PDF, images (PNG, JPEG, GIF, WEBP), DOCX, TXT, MD, CSV, JSON, XML, HTML. Skipped: ${skipped.join(', ') || 'none'}]`,
      filesAnalyzed: 0,
      skippedFiles: skipped,
    };
  }
  
  const filesAnalyzed = files.length - skipped.length;
  
  // Build the API request - extended thinking may not work with vision content,
  // so we try WITHOUT extended thinking first, then retry WITH it if that fails
  // Transport policy (2026-06-11 directive): NO wall-clock cap on model
  // completion. The previous 110s end-to-end ladder budget killed
  // healthy-but-slow vision runs — the codebase's own notes show the heavy
  // vision model legitimately spending "90s+ in upstream OPENROUTER
  // PROCESSING" (which is keep-alive traffic, i.e. a LIVE connection that a
  // total budget kills and an inactivity watchdog correctly tolerates).
  // The ladder is now bounded by transport liveness instead: a connect guard
  // on each fetch attempt (headers — reachability, never model time) and an
  // inactivity watchdog on stream reads (any bytes reset it, including
  // OpenRouter keep-alive comments). Caller aborts still tear everything
  // down immediately via ladderAbortController; the caller side (wave
  // reader / pre-orchestrator) stops WAITING on its own pacing schedule
  // without killing this analysis — late results land in the shared cache.
  const ladderAbortController = new AbortController();
  const onCallerAbort = () => ladderAbortController.abort(signal?.reason || new Error('caller aborted analysis'));
  if (signal) {
    if (signal.aborted) {
      ladderAbortController.abort(signal.reason || new Error('caller aborted analysis'));
    } else {
      signal.addEventListener('abort', onCallerAbort, { once: true });
    }
  }

  // Caller-abort guard between ladder rungs (replaces the old total-budget
  // check — rungs are finite and each is liveness-policed, so no deadline).
  const ensureNotAborted = (phase: string) => {
    if (ladderAbortController.signal.aborted) {
      throw new Error(`[DocAnalyzer] ${phase} aborted by caller`);
    }
  };

  // D14 slice 4 / A4 (2026-08-26 ruling follow-through): the route name is
  // the routing input — mode:'task' on anthropicProxy. The server stamps the
  // SSOT document-analysis ladder + the tier's configured per-rung effort;
  // no model and no thinking leave the browser. The output budget matches
  // the legacy THINKING branch (32000, not 8192): the server runs this at
  // the tier effort, and reasoning tokens count toward the completion budget
  // on the OpenRouter path. Slice 7 (2026-09-15): the rung OFFSET rides the
  // body — rung 1 is the text-only fast path (server-resolved, so an SSOT
  // re-ladder reaches it); the legacy body builder retired with the
  // deploy-skew fallback.
  const makeTaskRequestBody = (rung: number) => ({
    mode: 'task',
    route: 'document-analysis',
    ...(rung > 0 ? { rung } : {}),
    max_tokens: 32000,
    stream: true,
    system: DOCUMENT_ANALYSIS_PROMPT,
    messages: [
      {
        role: 'user',
        content,
      },
    ],
  });

  // Model selection.
  //
  // If every content block is plain text (i.e. the document was successfully
  // extracted client-side and there are NO image/vision blocks), we do NOT need
  // the heavy opus vision/reasoning model — it spends 90s+ in upstream
  // "OPENROUTER PROCESSING" before the first token on a large (48K-char) input,
  // which blows the 90s pre-orchestrator doc-read timeout and leaves the chat
  // stuck on "working" forever. A fast general model summarizes extracted text
  // in a few seconds. Reserve the opus vision model for genuine image/scanned
  // content where its multimodal capability is actually required.
  const contentIsTextOnly =
    Array.isArray(content) &&
    content.length > 0 &&
    content.every((c: any) => c?.type === 'text');
  // The fast text-only path rides the route's OWN first fallback rung as a
  // server-side rung OFFSET (D14 slice 7, 2026-09-15 — supersedes the
  // pending 'document-analysis-text' route recommendation: the offset needs
  // no hand-maintained twin tier and every SSOT re-ladder reaches it).
  const taskRung = contentIsTextOnly ? 1 : 0;
  // Display hint only — the server stamps the real ladder; this names the
  // rung the wire will lead with, for logs and the usage card.
  const taskDisplayModel =
    (contentIsTextOnly ? DOCUMENT_ANALYSIS_LADDER[1] : CLAUDE_VISION_MODEL) || CLAUDE_VISION_MODEL;
  console.log(`[DocAnalyzer] Route document-analysis rung=${taskRung} (textOnly=${contentIsTextOnly})`);

  const callClaude = async (model: string, rung: number = 0) => {
    ensureNotAborted(`claude:${model}`);
    const body: Record<string, unknown> = {
      ...makeTaskRequestBody(rung),
      meterSummary: DOC_ANALYSIS_METER_SUMMARY,
    };
    const startTime = Date.now();

    // Log request details for debugging
    const messages = body.messages as Array<{ content: unknown[] }>;
    console.log(`[DocAnalyzer] API Request - route document-analysis rung=${rung} (display ${model}), content blocks: ${messages[0].content.length}`);
    const contentTypes = (messages[0].content as Array<{ type?: string }>).map(c => c.type);
    console.log(`[DocAnalyzer] Content block types: ${contentTypes.join(', ')}`);

    // Build headers - include beta headers for PDFs when needed
    // Note: output-128k-2025-02-19 is only for Claude 3.7 Sonnet, not Claude 4.5 models
    const betaFeatures: string[] = [];
    // Add PDF beta header when there are PDF files to analyze
    if (hasPdfFiles) {
      betaFeatures.push('pdfs-2024-09-25');
    }

    // Headers for Firebase proxy - API key is handled server-side
    const accountHint = knownAccountHint();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      // API key is handled by the proxy server — and so is the wallet debit
      // (server meter, see ./proxyMeter).
      ...SERVER_METER_HEADERS,
      ...(accountHint ? { 'X-Account-Id': accountHint } : {}),
      // Who is calling — the signed-in user's ID token (security slice 6, ./proxyAuth).
      ...(await proxyAuthHeaders()),
      'X-Prest0n-Task': 'document-analysis',
    };
    // Only add beta header if there are beta features to include
    if (betaFeatures.length > 0) {
      headers['anthropic-beta'] = betaFeatures.join(',');
    }

    console.log(`[DocAnalyzer] API Headers: anthropic-beta=${headers['anthropic-beta'] || 'not set'}`);

    // Helper to delay for exponential backoff
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // Retry logic for transient errors
    const maxRetries = 3;
    let response: Response | null = null;
    let lastFetchErr: unknown = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        ensureNotAborted(`claude:${model}:retry_backoff`);
        const backoffMs = Math.pow(2, attempt - 1) * 1000;
        console.log(`[DocAnalyzer] Retrying after ${backoffMs}ms (attempt ${attempt + 1}/${maxRetries})`);
        await delay(backoffMs);
      }

      // Per-attempt connect guard — bounds time-to-HEADERS only (transport
      // reachability; with stream:true the model hasn't emitted anything
      // yet, so an expiry loses no model work and is retried). Chained to
      // the ladder controller so caller aborts still cancel the fetch.
      const attemptController = new AbortController();
      const onLadderAbortForAttempt = () => attemptController.abort(ladderAbortController.signal.reason);
      if (ladderAbortController.signal.aborted) {
        attemptController.abort(ladderAbortController.signal.reason);
      } else {
        ladderAbortController.signal.addEventListener('abort', onLadderAbortForAttempt, { once: true });
      }
      let connectGuardFired = false;
      const connectTimer = setTimeout(() => {
        connectGuardFired = true;
        attemptController.abort(new Error(`connect guard: headers not received within ${DOC_ANALYZER_CONNECT_GUARD_MS / 1000}s`));
      }, DOC_ANALYZER_CONNECT_GUARD_MS);

      try {
        // Use Firebase proxy to avoid CORS issues
        const proxyUrl = getAnthropicProxyUrl();
        response = await fetch(proxyUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: attemptController.signal,
        });

        clearTimeout(connectTimer);

        // If successful or non-retryable error, break out of retry loop
        if (response.ok || (response.status !== 500 && response.status !== 502 && response.status !== 503 && response.status !== 429)) {
          break;
        }

        console.log(`[DocAnalyzer] Transient error ${response.status}, will retry...`);
        // For retryable errors, continue to next attempt (unless last attempt)
        if (attempt === maxRetries - 1) {
          break; // Use this response for error handling
        }
      } catch (fetchErr) {
        clearTimeout(connectTimer);
        ladderAbortController.signal.removeEventListener('abort', onLadderAbortForAttempt);
        // Caller abort is terminal — never retry past it.
        if (ladderAbortController.signal.aborted) {
          throw fetchErr;
        }
        lastFetchErr = connectGuardFired
          ? new Error(`connect guard: headers not received within ${DOC_ANALYZER_CONNECT_GUARD_MS / 1000}s (transport guard, not a model-time cap)`)
          : fetchErr;
        console.error(`[DocAnalyzer] Network error (attempt ${attempt + 1}):`, lastFetchErr);
        if (attempt === maxRetries - 1) {
          const err: Error & { code?: string } = new Error(`Network error calling Apex: ${String(lastFetchErr)}`);
          err.code = 'NETWORK';
          throw err;
        }
      }
    }

    if (!response) {
      const err: Error & { code?: string } = new Error(`Network error calling Apex: ${String(lastFetchErr)}`);
      err.code = 'NETWORK';
      throw err;
    }

    console.log(`[DocAnalyzer] API Response status: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      let errorData: Record<string, unknown> = {};
      let rawText = '';
      try {
        rawText = await response.text();
        errorData = JSON.parse(rawText);
      } catch {
        console.error('[DocAnalyzer] Failed to parse error response:', rawText);
      }
      const message = (errorData?.error as Record<string, unknown>)?.message || `Apex request failed (${response.status})`;
      console.error('[DocAnalyzer] API error:', message);
      console.error('[DocAnalyzer] Full error response:', errorData);
      console.error('[DocAnalyzer] Raw response:', rawText);
      const err: Error & { status?: number; code?: string; model?: string } = new Error(String(message));
      err.status = response.status;
      err.code = String((errorData?.error as Record<string, unknown>)?.type || `HTTP_${response.status}`);
      err.model = model;
      throw err;
    }

    // ── STREAMING READER ─────────────────────────────────────────────────────
    // We use streaming (stream: true in the body) so the HTTP connection stays
    // alive for large PDFs. Non-streaming timed out at ~120s on OpenRouter.
    // SSE events: Anthropic format (content_block_delta / message_delta) OR
    // OpenAI/OpenRouter format (choices[0].delta.content).
    const responseTimeMs = Date.now() - startTime;

    if (!response.body) {
      throw new Error('No response body for streaming analysis');
    }

    let analysisText = '';
    let usageInputTokens: number | undefined;
    let usageOutputTokens: number | undefined;

    ensureNotAborted(`claude:${model}:stream`);

    // Inactivity-policed read loop — total stream duration is unbounded; the
    // watchdog only trips when NO bytes arrive for the whole window (any
    // traffic counts, including OpenRouter keep-alive comments and SSE
    // pings). Replaces the old race against the remaining total budget,
    // which was a model-time cap.
    await (async () => {
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        let readResult: ReadableStreamReadResult<Uint8Array>;
        let inactivityTimer: ReturnType<typeof setTimeout> | undefined;
        try {
          readResult = await Promise.race([
            reader.read(),
            new Promise<never>((_, reject) => {
              inactivityTimer = setTimeout(
                () => reject(new Error(
                  `Document analysis stream inactivity timeout - no data received for ${DOC_ANALYZER_STREAM_INACTIVITY_MS / 1000}s ` +
                  '(transport liveness check, not a model-time cap)'
                )),
                DOC_ANALYZER_STREAM_INACTIVITY_MS,
              );
            }),
          ]);
        } finally {
          if (inactivityTimer) clearTimeout(inactivityTimer);
        }
        const { done, value } = readResult;
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Process all complete SSE lines in the buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // last (possibly incomplete) line stays in buffer

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || trimmed === 'data: [DONE]') continue;
          if (!trimmed.startsWith('data: ')) continue;

          let parsed: Record<string, unknown>;
          try {
            parsed = JSON.parse(trimmed.slice(6));
          } catch {
            continue;
          }

          // Anthropic SSE format
          const eventType = parsed.type as string | undefined;
          if (eventType === 'content_block_delta') {
            const delta = parsed.delta as Record<string, unknown> | undefined;
            if (delta?.type === 'text_delta' && typeof delta.text === 'string') {
              analysisText += delta.text;
            }
          } else if (eventType === 'message_delta') {
            const usage = parsed.usage as Record<string, unknown> | undefined;
            if (usage?.output_tokens) usageOutputTokens = usage.output_tokens as number;
          } else if (eventType === 'message_start') {
            const msg = parsed.message as Record<string, unknown> | undefined;
            const usage = msg?.usage as Record<string, unknown> | undefined;
            if (usage?.input_tokens) usageInputTokens = usage.input_tokens as number;
          } else {
            // OpenRouter / OpenAI SSE format
            const choices = parsed.choices as Array<Record<string, unknown>> | undefined;
            if (choices?.[0]) {
              const delta = choices[0].delta as Record<string, unknown> | undefined;
              if (typeof delta?.content === 'string') {
                analysisText += delta.content;
              }
              // Usage in final chunk
              const chunkUsage = parsed.usage as Record<string, unknown> | undefined;
              if (chunkUsage?.prompt_tokens) usageInputTokens = chunkUsage.prompt_tokens as number;
              if (chunkUsage?.completion_tokens) usageOutputTokens = chunkUsage.completion_tokens as number;
            }
          }
        }
      }
    })();

    // Validate we got actual content
    if (!analysisText || analysisText.length < 10) {
      console.error(`[DocAnalyzer] Empty or minimal analysis returned via streaming (${analysisText.length} chars)`);
    }

    const tokenUsage: TokenUsageMetadata = {
      inputTokens: usageInputTokens,
      outputTokens: usageOutputTokens,
      totalTokens: ((usageInputTokens || 0) + (usageOutputTokens || 0)) || undefined,
      model,
      provider: 'claude',
      responseTimeMs: Date.now() - startTime,
      timestamp: new Date(),
    };

    console.log(`[DocAnalyzer] Streaming analysis complete: ${analysisText.length} chars, tokens: ${tokenUsage.inputTokens}/${tokenUsage.outputTokens}, model: ${model}`);

    return {
      analysisText,
      tokenUsage,
    };
  };

  try {
  // D14 slice 7 (2026-09-15, owner "proceed"): the task route carries BOTH
  // content classes — the legacy client ladder, the cross-provider descent,
  // and the deploy-skew fallback are retired. The server's stamped models
  // array already descends the SSOT tier-11 ladder, so a failure here is the
  // route's own verdict and surfaces honestly. Aborts and auth errors
  // propagate as before.
  console.log(`[DocAnalyzer] [Task] Attempting route document-analysis rung=${taskRung}`);
  const { analysisText, tokenUsage } = await callClaude(taskDisplayModel, taskRung);
  console.log('[DocAnalyzer] [Task] Success via route document-analysis');
  return {
    analysis: analysisText,
    filesAnalyzed,
    skippedFiles: skipped,
    tokenUsage,
    extractedFullText,
    fullTextSource,
  };
  } finally {
    if (signal) {
      signal.removeEventListener('abort', onCallerAbort);
    }
  }
}

/**
 * Check if any files in the array are analyzable documents/images
 */
export function hasAnalyzableFiles(files: File[] | undefined): boolean {
  if (!files || files.length === 0) return false;
  return files.some(isSupportedFileType);
}
